//src/services/api.js
import axios from 'axios';

// Base API URL - adjust based on your backend server
const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000/api';

// Create axios instance with default config
const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor to add auth token
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('access_token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor to handle token refresh
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    // If error is 401 and we haven't retried yet
    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;

      try {
        const refreshToken = localStorage.getItem('refresh_token');
        if (refreshToken) {
          const response = await axios.post(`${API_BASE_URL}/auth/token/refresh/`, {
            refresh: refreshToken,
          });

          const { access } = response.data;
          localStorage.setItem('access_token', access);

          // Retry original request with new token
          originalRequest.headers.Authorization = `Bearer ${access}`;
          return api(originalRequest);
        }
      } catch (refreshError) {
        // Refresh token failed, logout user
        localStorage.removeItem('access_token');
        localStorage.removeItem('refresh_token');
        localStorage.removeItem('user');
        window.location.href = '/login';
        return Promise.reject(refreshError);
      }
    }

    return Promise.reject(error);
  }
);

// Authentication API functions
export const authAPI = {
  login: async (username, password) => {
    const response = await api.post('/auth/login/', { username, password });
    return response.data;
  },

  register: async (userData) => {
    const response = await api.post('/auth/register/', userData);
    return response.data;
  },

  logout: async (refreshToken) => {
    const response = await api.post('/auth/logout/', { refresh_token: refreshToken });
    return response.data;
  },

  getCurrentUser: async () => {
    const response = await api.get('/auth/me/');
    return response.data;
  },

  changePassword: async (passwordData) => {
    const response = await api.post('/auth/change-password/', passwordData);
    return response.data;
  },

  updateProfile: async (profileData) => {
    const response = await api.patch('/auth/profile/', profileData);
    return response.data;
  },

  uploadProfilePicture: async (file) => {
    const formData = new FormData();
    formData.append('profile_picture', file);
    const response = await api.patch('/auth/profile/', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data;
  },

  deleteProfilePicture: async () => {
    const response = await api.patch('/auth/profile/', { delete_profile_picture: 'true' });
    return response.data;
  },

  getBankDetail: async () => {
    const response = await api.get('/auth/profile/bank-detail/');
    return response.data;
  },

  saveBankDetail: async (bankData) => {
    const response = await api.put('/auth/profile/bank-detail/', bankData);
    return response.data;
  },
};

// Department API functions
export const departmentAPI = {
  getDepartments: async () => {
    let allDepartments = [];
    let nextUrl = '/auth/departments/';

    while (nextUrl) {
      const response = await api.get(nextUrl);
      const data = response.data;

      if (data.results && Array.isArray(data.results)) {
        allDepartments = [...allDepartments, ...data.results];
        nextUrl = data.next ? new URL(data.next).pathname + new URL(data.next).search : null;
      } else if (Array.isArray(data)) {
        return data;
      } else {
        break;
      }
    }

    return allDepartments;
  },

  getFacultyByDepartment: async (departmentId) => {
    let allFaculty = [];
    let nextUrl = `/auth/departments/${departmentId}/faculty/`;

    while (nextUrl) {
      const response = await api.get(nextUrl);
      const data = response.data;

      if (data.results && Array.isArray(data.results)) {
        allFaculty = [...allFaculty, ...data.results];
        nextUrl = data.next ? new URL(data.next).pathname + new URL(data.next).search : null;
      } else if (Array.isArray(data)) {
        return data;
      } else {
        break;
      }
    }

    return allFaculty;
  },
};

export const principalAPI = {
  getOPDMetrics: async () => {
    const response = await api.get('/auth/reception/tokens/').catch(() => ({ data: [] }));
    return response.data;
  },
  getClasses: async (params = {}) => {
    const response = await api.get('/auth/principal/classes/', { params });
    return response.data;
  },

  getAuditLogs: async (params = {}) => {
    const response = await api.get('/auth/principal/audit-logs/', { params });
    return response.data;
  },

  // ── Dashboard ─────────────────────────────────────────────────────────────
  getDashboardStats: async () => {
    const response = await api.get('/auth/principal/dashboard/stats/');
    return response.data;
  },

  // ── Users ─────────────────────────────────────────────────────────────────
  /**
   * List all users with complete financial detail.
   * @param {{ role?, status?, department_id?, course?, is_active?, search?, page?, page_size? }} params
   */
  getUserFinancialDetail: async (userId) => {
    const response = await api.get(`/auth/finance/users/${userId}/`);
    return response.data;
  },

  getUsers: async (params = {}) => {
    const response = await api.get('/auth/principal/users/', { params });
    return response.data;
  },

  getUserDetail: async (userId) => {
    const response = await api.get(`/auth/principal/users/${userId}/`);
    return response.data;
  },

  // ── Departments ───────────────────────────────────────────────────────────
  getDepartments: async () => {
    const response = await api.get('/auth/principal/departments/');
    return response.data;
  },

  // ── Faculty ───────────────────────────────────────────────────────────────
  /**
   * @param {{ department_id?, subrole?, search?, page? }} params
   */
  getFaculty: async (params = {}) => {
    const response = await api.get('/auth/principal/faculty/', { params });
    return response.data;
  },

  // ── Students ──────────────────────────────────────────────────────────────
  /**
   * @param {{ course?, department_id?, overdue?, search?, page? }} params
   */
  getStudents: async (params = {}) => {
    const response = await api.get('/auth/principal/students/', { params });
    return response.data;
  },

  // ── Leaves ────────────────────────────────────────────────────────────────
  /**
   * @param {{ status?, leave_type?, department_id?, role?, from_date?, to_date? }} params
   */
  getLeaves: async (params = {}) => {
    const response = await api.get('/auth/principal/leaves/', { params });
    return response.data;
  },

  getLeaveStats: async () => {
    const response = await api.get('/auth/principal/leaves/stats/');
    return response.data;
  },

  // ── Payroll ───────────────────────────────────────────────────────────────
  /**
   * @param {{ month?, year?, status?, role? }} params
   */
  getPayroll: async (params = {}) => {
    const response = await api.get('/auth/principal/payroll/', { params });
    return response.data;
  },

  getPayrollSummary: async (month, year) => {
    const response = await api.get('/auth/principal/payroll/summary/', {
      params: { month, year },
    });
    return response.data;
  },

  // ── Grievances ────────────────────────────────────────────────────────────
  /**
   * @param {{ status?, priority?, search?, all? }} params
   *   all=1 returns ALL grievances (not just escalated-to-principal)
   */
  getGrievances: async (params = {}) => {
    const response = await api.get('/auth/principal/grievances/', { params });
    return response.data;
  },

  getGrievanceDetail: async (id) => {
    const response = await api.get(`/auth/principal/grievances/${id}/`);
    return response.data;
  },

  updateGrievanceStatus: async (id, payload) => {
    const response = await api.post(`/auth/principal/grievances/${id}/update-status/`, payload);
    return response.data;
  },

  respondToGrievance: async (id, formData) => {
    const response = await api.post(`/auth/principal/grievances/${id}/respond/`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data;
  },

  // ── Financial ─────────────────────────────────────────────────────────────
  getFinancialSummary: async () => {
    const response = await api.get('/auth/principal/financial/summary/');
    return response.data;
  },

  // ── OPD ───────────────────────────────────────────────────────────────────
  getOPDOverview: async () => {
    const response = await api.get('/auth/principal/opd/overview/');
    return response.data;
  },

  // ── Attendance ────────────────────────────────────────────────────────────
  getAttendanceOverview: async () => {
    const response = await api.get('/auth/principal/attendance/overview/');
    return response.data;
  },
};

// Admission API
export const admissionAPI = {
  // Public — no auth needed
  submitApplication: async (formData) => {
    const response = await axios.post(`${API_BASE_URL}/auth/admissions/apply/`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data;
  },

  // Principal-only
  getApplications: async (params = {}) => {
    const response = await api.get('/auth/principal/admissions/', { params });
    return response.data;
  },

  getApplicationDetail: async (id) => {
    const response = await api.get(`/auth/principal/admissions/${id}/`);
    return response.data;
  },

  reviewApplication: async (id, payload) => {
    const response = await api.post(`/auth/principal/admissions/${id}/review/`, payload);
    return response.data;
  },

  getMeritList: async () => {
    const response = await api.get('/auth/principal/admissions/merit-list/');
    return response.data;
  },

  // Toggle public visibility of the merit list
  publishMeritList: async (publish = true) => {
    const response = await api.post('/auth/principal/admissions/merit-list/publish/', { publish });
    return response.data;
  },

  // Check current publish status
  getMeritListStatus: async () => {
    const response = await api.get('/auth/principal/admissions/merit-list/status/');
    return response.data;
  },

  // Public endpoint — no auth required
  getPublicMeritList: async () => {
    const response = await axios.get(`${API_BASE_URL}/auth/admissions/merit-list/public/`);
    return response.data;
  },
};


export const attendanceAPI = {

  /**
   * Punch in or out.  Backend auto-determines the direction.
   * POST /auth/attendance/punch/
   */
  punch: async (notes = '') => {
    const response = await api.post('/auth/attendance/punch/', { notes });
    return response.data;
  },

  /**
   * Today's attendance status for the current user.
   * GET /auth/attendance/today/
   */
  getToday: async () => {
    const response = await api.get('/auth/attendance/today/');
    return response.data;
  },

  /**
   * Monthly attendance records + summary.
   * GET /auth/attendance/monthly/?month=3&year=2026&user_id=<optional>
   */
  getMonthly: async (month, year, userId = null) => {
    const params = { month, year };
    if (userId) params.user_id = userId;
    const response = await api.get('/auth/attendance/monthly/', { params });
    return response.data;
  },

  /**
   * Full-year attendance — used by the calendar component.
   * GET /auth/attendance/yearly/?year=2026&user_id=<optional>
   */
  getYearly: async (year, userId = null) => {
    const params = { year };
    if (userId) params.user_id = userId;
    const response = await api.get('/auth/attendance/yearly/', { params });
    return response.data;
  },

  /**
   * Raw punch logs for a specific date or month.
   * GET /auth/attendance/punch-logs/?date=2026-03-30   (or ?month=3&year=2026)
   */
  getPunchLogs: async ({ date = null, month = null, year = null, userId = null } = {}) => {
    const params = {};
    if (date)   params.date  = date;
    if (month)  params.month = month;
    if (year)   params.year  = year;
    if (userId) params.user_id = userId;
    const response = await api.get('/auth/attendance/punch-logs/', { params });
    return response.data;
  },

  /**
   * Admin: force-recompute a user's attendance for a specific date.
   * POST /auth/attendance/recalc/
   */
  recalculate: async (userId, date) => {
    const response = await api.post('/auth/attendance/recalc/', {
      user_id: userId,
      date,
    });
    return response.data;
  },

  /**
   * Admin/Principal: list attendance for any user.
   * GET /auth/attendance/admin/?user_id=5&month=3&year=2026
   */
  adminList: async (userId, month = null, year = null) => {
    const params = { user_id: userId };
    if (month) params.month = month;
    if (year)  params.year  = year;
    const response = await api.get('/auth/attendance/admin/', { params });
    return response.data;
  },
};

// HOD API
export const hodAPI = {
  getDashboardStats: async () => {
    const response = await api.get('/auth/hod/dashboard/stats/');
    return response.data;
  },

  getProfessors: async (params = {}) => {
    const response = await api.get('/auth/hod/professors/', { params });
    return response.data;
  },

  getProfessorDetail: async (professorId) => {
    const response = await api.get(`/auth/hod/professors/${professorId}/`);
    return response.data;
  },

  getStudents: async (params = {}) => {
    const response = await api.get('/auth/hod/students/', { params });
    return response.data;
  },

  getStudentsByProfessor: async () => {
    const response = await api.get('/auth/hod/students/by-professor/');
    return response.data;
  },

  reassignStudent: async (studentId, newProfessorId, reason = '') => {
    const response = await api.post('/auth/hod/students/reassign/', {
      student_id: studentId,
      new_professor_id: newProfessorId,
      reason: reason
    });
    return response.data;
  },

  getReassignmentHistory: async () => {
    const response = await api.get('/auth/hod/reassignments/history/');
    return response.data;
  },

  getClassAssignments: async (params = {}) => {
    const response = await api.get('/auth/hod/class-assignments/', { params });
    return response.data;
  },

  createClassAssignment: async (assignmentData) => {
    const response = await api.post('/auth/hod/class-assignments/', assignmentData);
    return response.data;
  },

  getClassAssignmentDetail: async (assignmentId) => {
    const response = await api.get(`/auth/hod/class-assignments/${assignmentId}/`);
    return response.data;
  },

  updateClassAssignment: async (assignmentId, assignmentData) => {
    const response = await api.patch(`/auth/hod/class-assignments/${assignmentId}/`, assignmentData);
    return response.data;
  },

  deleteClassAssignment: async (assignmentId) => {
    const response = await api.delete(`/auth/hod/class-assignments/${assignmentId}/`);
    return response.data;
  },

  activateAssignment: async (assignmentId) => {
    const response = await api.post(`/auth/hod/class-assignments/${assignmentId}/activate/`);
    return response.data;
  },

  completeAssignment: async (assignmentId) => {
    const response = await api.post(`/auth/hod/class-assignments/${assignmentId}/complete/`);
    return response.data;
  },
};

// deanAPI
export const deanAPI = {
  getDashboardStats: async () => {
    try {
      const [adminStats, finStats, grievList] = await Promise.all([
        api.get('/auth/admin/stats/').then(r => r.data).catch(() => ({})),
        api.get('/auth/finance/dashboard/stats/').then(r => r.data).catch(() => ({})),
        api.get('/auth/grievances/', { params: { status: 'ESCALATED' } }).then(r => r.data).catch(() => []),
      ]);
      const grievances = Array.isArray(grievList) ? grievList : (grievList?.results || []);
      const deanGrievances = grievances.filter(g => g.escalated_to === 'DEAN');

      // Count pending leaves via payroll endpoint (read-only proxy)
      let pendingLeaves = 0;
      try {
        const lv = await api.get('/auth/leave/stats/').then(r => r.data).catch(() => ({}));
        pendingLeaves = lv.total_pending || 0;
      } catch (_) {}

      return {
        ...adminStats,
        total_revenue_collected:  finStats.total_revenue_collected,
        total_pending_fees:       finStats.total_pending_fees,
        monthly_salary_expense:   finStats.monthly_salary_expense,
        transactions_today:       finStats.transactions_today,
        amount_collected_today:   finStats.amount_collected_today,
        total_staff:              finStats.total_staff,
        escalated_to_dean:        deanGrievances.length,
        pending_leaves:           pendingLeaves,
        opd_tokens_today:         0, // populated below
        lab_revenue_today:        0,
      };
    } catch (e) {
      console.error('deanAPI.getDashboardStats:', e);
      return {};
    }
  },

  getDepartments: async () => {
    const response = await api.get('/auth/departments/');
    return Array.isArray(response.data) ? response.data : (response.data?.results || []);
  },

  getUserFinancialDetail: async (userId) => {
    const response = await api.get(`/auth/finance/users/${userId}/`);
    return response.data;
  },

  getUsers: async (params = {}) => {
    const response = await api.get('/auth/admin/users/', { params });
    return response.data;
  },

  getUserDetail: async (userId) => {
    const response = await api.get(`/auth/admin/users/${userId}/`);
    return response.data;
  },

  getLeaves: async (params = {}) => {
    // Dean reads all leaves via finance users endpoint — or use leave/stats
    // The backend needs to permit DEAN on this endpoint (see views.py changes)
    const response = await api.get('/auth/admin/users/', { params: { page_size: 1, ...params } })
      .catch(() => api.get('/auth/leave/my-leaves/', { params }));
    // If admin endpoint works, fetch actual leaves using finance endpoint approach
    try {
      const leaveResponse = await api.get('/auth/leave/department/', { params });
      return leaveResponse.data;
    } catch (_) {
      // Fallback: fetch leave applications visible to current user
      return response.data;
    }
  },

  getLeaveStats: async () => {
    const response = await api.get('/auth/leave/stats/');
    return response.data;
  },

  getPayroll: async (params = {}) => {
    const response = await api.get('/auth/payroll/records/', { params });
    return response.data;
  },

  getPayrollSummary: async (month, year) => {
    const response = await api.get('/auth/payroll/summary/', { params: { month, year } });
    return response.data;
  },

  getGrievances: async (params = {}) => {
    const response = await api.get('/auth/grievances/', { params });
    return response.data;
  },

  getGrievanceDetail: async (id) => {
    const response = await api.get(`/auth/grievances/${id}/`);
    return response.data;
  },

  getGrievanceStats: async () => {
    const response = await api.get('/auth/grievances/officer/stats/');
    return response.data;
  },

  updateGrievanceStatus: async (id, payload) => {
    const response = await api.post(`/auth/grievances/${id}/update-status/`, payload);
    return response.data;
  },

  respondToGrievance: async (id, formData) => {
    const response = await api.post(`/auth/grievances/${id}/respond/`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data;
  },

  getFinancialSummary: async () => {
    const [finStats, labStats] = await Promise.all([
      api.get('/auth/finance/dashboard/stats/').then(r => r.data).catch(() => ({})),
      api.get('/auth/lab/billing/stats/').then(r => r.data).catch(() => null),
    ]);

    // Aggregate course breakdown from finance users
    let courseBreakdown = [];
    try {
      const usersResp = await api.get('/auth/finance/users/', { params: { role: 'STUDENT', page_size: 500 } });
      const studentList = Array.isArray(usersResp.data) ? usersResp.data : (usersResp.data?.results || []);
      const courseMap = {};
      studentList.forEach(u => {
        const course = u.profile?.course || 'UNKNOWN';
        if (!courseMap[course]) courseMap[course] = { user__profile__course: course, total_students: 0, total_collected: 0, total_pending: 0 };
        courseMap[course].total_students++;
        courseMap[course].total_collected += parseFloat(u.financial_record?.tuition_paid || 0);
        courseMap[course].total_pending   += parseFloat(u.financial_record?.tuition_pending || 0);
      });
      courseBreakdown = Object.values(courseMap);
    } catch (_) {}

    const labIncome = labStats?.total_revenue || 0;
    const netBalance = (
      parseFloat(finStats.total_revenue_collected || 0) +
      parseFloat(labIncome) -
      parseFloat(finStats.monthly_salary_expense || 0)
    ).toFixed(2);

    return {
      total_tuition_fee:     finStats.total_revenue_collected,
      total_tuition_paid:    finStats.total_revenue_collected,
      total_tuition_pending: finStats.total_pending_fees,
      total_monthly_salary:  finStats.monthly_salary_expense,
      overdue_count:         finStats.total_overdue_count,
      transactions_today:    finStats.transactions_today,
      fully_paid_students:   0,
      lab_income:            labIncome,
      net_balance:           netBalance,
      total_expenses:        finStats.monthly_salary_expense,
      course_breakdown:      courseBreakdown,
    };
  },

  getLabBillingStats: async () => {
    const response = await api.get('/auth/lab/billing/stats/');
    return response.data;
  },

  getOPDOverview: async () => {
    try {
      const response = await api.get('/auth/reception/tokens/');
      const tokens = Array.isArray(response.data) ? response.data : (response.data?.results || []);
      return {
        total_tokens:     tokens.length,
        completed:        tokens.filter(t => t.status === 'COMPLETED').length,
        waiting:          tokens.filter(t => t.status === 'WAITING').length,
        in_progress:      tokens.filter(t => t.status === 'IN_PROGRESS').length,
        completion_rate:  tokens.length > 0
          ? Math.round(tokens.filter(t => t.status === 'COMPLETED').length / tokens.length * 100)
          : 0,
        lab_revenue_today: 0,
      };
    } catch (_) { return null; }
  },

  getAttendanceOverview: () => Promise.resolve(null),
};

// Faculty API functions
export const facultyAPI = {
  getDashboardStats: async () => {
    const response = await api.get('/auth/faculty/dashboard/stats/');
    return response.data;
  },

  getUpcomingClasses: async () => {
    const response = await api.get('/auth/faculty/classes/upcoming/');
    return response.data;
  },

  getClasses: async () => {
    const response = await api.get('/auth/faculty/classes/');
    return response.data;
  },

  createClass: async (classData) => {
    try {
      const response = await api.post('/auth/faculty/classes/create/', classData);
      return response.data;
    } catch (error) {
      console.error('Create class error:', error.response?.data);
      throw error;
    }
  },

  getClassDetails: async (classId) => {
    const response = await api.get(`/auth/faculty/classes/${classId}/`);
    return response.data;
  },

  updateClass: async (classId, classData) => {
    const response = await api.patch(`/auth/faculty/classes/${classId}/`, classData);
    return response.data;
  },

  deleteClass: async (classId) => {
    const response = await api.delete(`/auth/faculty/classes/${classId}/`);
    return response.data;
  },

  getStudentsByBatch: async (batch, departmentId = null) => {
    const params = { batch };
    if (departmentId) params.department_id = departmentId;
    const response = await api.get('/auth/faculty/students/by-batch/', { params });
    return response.data;
  },

  markAttendance: async (classId, attendanceRecords) => {
    const response = await api.post('/auth/faculty/attendance/mark/', {
      class_id: classId,
      attendance_records: attendanceRecords
    });
    return response.data;
  },

  getClassAttendance: async (classId) => {
    const response = await api.get(`/auth/faculty/classes/${classId}/attendance/`);
    return response.data;
  },

  getPendingEvaluations: async () => {
    const response = await api.get('/auth/faculty/evaluations/pending/');
    return response.data;
  },

  getAllEvaluations: async () => {
    const response = await api.get('/auth/faculty/evaluations/');
    return response.data;
  },

  createEvaluation: async (evaluationData) => {
    const response = await api.post('/auth/faculty/evaluations/create/', evaluationData);
    return response.data;
  },

  getAttendanceRecords: async () => {
    const response = await api.get('/auth/faculty/attendance/records/');
    return response.data;
  },

  getPGStudents: async (params = {}) => {
    const response = await api.get('/auth/faculty/pg-students/', { params });
    return response.data;
  },

  getStudentPerformance: async () => {
    const response = await api.get('/auth/faculty/students/performance/');
    return response.data;
  },

  getHODAssignments: async () => {
    const response = await api.get('/auth/faculty/hod-assignments/');
    return response.data;
  },

  getActiveHODAssignments: async () => {
    const response = await api.get('/auth/faculty/hod-assignments/active/');
    return response.data;
  },
};

// Student API functions
export const studentAPI = {
  getDashboardStats: async () => {
    const response = await api.get('/auth/student/dashboard/stats/');
    return response.data;
  },

  getUpcomingExams: async () => {
    const response = await api.get('/auth/student/exams/upcoming/');
    return response.data;
  },

  getClinicalRotations: async () => {
    const response = await api.get('/auth/student/clinical-rotations/');
    return response.data;
  },

  getRecentAttendance: async () => {
    const response = await api.get('/auth/student/attendance/recent/');
    return response.data;
  },

  getAnnouncements: async () => {
    const response = await api.get('/auth/student/announcements/');
    return response.data;
  },

  getUpcomingClasses: async () => {
    const response = await api.get('/auth/student/classes/upcoming/');
    return response.data;
  },

  getClassAssignments: async () => {
    const response = await api.get('/auth/student/class-assignments/');
    return response.data;
  },

  getActiveClassAssignments: async () => {
    const response = await api.get('/auth/student/class-assignments/active/');
    return response.data;
  },
};

// Admin API functions
export const adminAPI = {
  getDepartments: async () => {
    const response = await api.get('/auth/departments/');
    return Array.isArray(response.data) ? response.data : (response.data?.results || []);
  },
  getStats: async () => {
    const response = await api.get('/auth/admin/stats/');
    return response.data;
  },

  getAnalyticsData: async () => {
    const response = await api.get('/auth/admin/analytics/data/');
    return response.data;
  },

  getUserFinancialDetail: async (userId) => {
    const response = await api.get(`/auth/finance/users/${userId}/`);
    return response.data;
  },

  getUsers: async (params = {}) => {
    const response = await api.get('/auth/admin/users/', { params });
    return response.data;
  },

  getUserDetails: async (userId) => {
    const response = await api.get(`/auth/admin/users/${userId}/`);
    return response.data;
  },

  updateUser: async (userId, userData) => {
    const response = await api.patch(`/auth/admin/users/${userId}/`, userData);
    return response.data;
  },

  deleteUser: async (userId) => {
    const response = await api.delete(`/auth/admin/users/${userId}/`);
    return response.data;
  },

  getRecentUsers: async () => {
    const response = await api.get('/auth/admin/recent-users/');
    return response.data;
  },

  getPendingAdmissions: async () => {
    const response = await api.get('/auth/admin/pending-admissions/');
    return response.data;
  },

  approveUser: async (userId) => {
    const response = await api.post('/auth/admin/approve-reject/', {
      user_id: userId,
      action: 'approve',
    });
    return response.data;
  },

  rejectUser: async (userId, reason = '') => {
    const response = await api.post('/auth/admin/approve-reject/', {
      user_id: userId,
      action: 'reject',
      rejection_reason: reason,
    });
    return response.data;
  },

  getLeavePolicies: async () => {
    const response = await api.get('/auth/admin/leave-policies/');
    return response.data;
  },

  createLeavePolicy: async (policyData) => {
    const response = await api.post('/auth/admin/leave-policies/', policyData);
    return response.data;
  },

  updateLeavePolicy: async (policyId, policyData, syncBalances = false) => {
    const url = `/auth/admin/leave-policies/${policyId}/${syncBalances ? '?sync_balances=1' : ''}`;
    const response = await api.patch(url, policyData);
    return response.data;
  },

  deleteLeavePolicy: async (policyId) => {
    const response = await api.delete(`/auth/admin/leave-policies/${policyId}/`);
    return response.data;
  },

  bulkUpdateLeaveBalances: async (data) => {
    const response = await api.post('/auth/admin/leave-balances/bulk-update/', data);
    return response.data;
  },

  getAllLeaveBalances: async (params = {}) => {
    const response = await api.get('/auth/admin/leave-balances/', { params });
    return response.data;
  },
};

export const leaveAPI = {
  applyLeave: async (leaveData) => {
    const response = await api.post('/auth/leave/apply/', leaveData);
    return response.data;
  },

  getMyLeaves: async () => {
    const response = await api.get('/auth/leave/my-leaves/');
    return response.data;
  },

  getPendingApprovals: async (statusFilter = 'PENDING') => {
    const response = await api.get('/auth/leave/pending-approvals/', {
      params: { status: statusFilter }
    });
    return response.data;
  },

  approveRejectLeave: async (data) => {
    const response = await api.post('/auth/leave/approve/', {
      leave_id: data.leave_id,
      action: String(data.action || '').toLowerCase(),
      rejection_reason: data.rejection_reason || ''
    });
    return response.data;
  },

  getLeaveStats: async () => {
    const response = await api.get('/auth/leave/stats/');
    return response.data;
  },

  getLeaveDetails: async (leaveId) => {
    const response = await api.get(`/auth/leave/${leaveId}/`);
    return response.data;
  },

  getDepartmentLeaves: async (statusFilter = null) => {
    const params = statusFilter ? { status: statusFilter } : {};
    const response = await api.get('/auth/leave/department/', { params });
    return response.data;
  },

  checkBalance: async (leaveType, fromDate, toDate) => {
    const response = await api.post('/auth/leave/check-balance/', {
      leave_type: leaveType,
      from_date: fromDate,
      to_date: toDate
    });
    return response.data;
  },

  getMyBalances: async (year = null) => {
    const params = year ? { year } : {};
    const response = await api.get('/auth/leave/my-balances/', { params });
    return response.data;
  },
};

export const financeAPI = {
  getDashboardStats: async () => {
    const response = await api.get('/auth/finance/dashboard/stats/');
    return response.data;
  },

  getUserFinancialDetail: async (userId) => {
    const response = await api.get(`/auth/finance/users/${userId}/`);
    return response.data;
  },

  getUsers: async (params = {}) => {
    const response = await api.get('/auth/finance/users/', { params });
    return response.data;
  },

  updateFinancialRecord: async (userId, data) => {
    const response = await api.patch(`/auth/finance/users/${userId}/update/`, data);
    return response.data;
  },

  recordPayment: async (paymentData) => {
    const response = await api.post('/auth/finance/payment/record/', paymentData);
    return response.data;
  },

  getPaymentHistory: async (params = {}) => {
    const response = await api.get('/auth/finance/payment/history/', { params });
    return response.data;
  },

  getFeeStructures: async () => {
    const response = await api.get('/auth/finance/fee-structures/');
    return response.data;
  },

  getAllFeeStructures: async () => {
    const response = await api.get('/auth/finance/fee-structures/all/');
    return response.data;
  },

  createFeeStructure: async (data) => {
    const response = await api.post('/auth/finance/fee-structures/all/', data);
    return response.data;
  },

  updateFeeStructureDetail: async (id, data) => {
    const response = await api.patch(`/auth/finance/fee-structures/all/${id}/`, data);
    return response.data;
  },

  deleteFeeStructure: async (id) => {
    const response = await api.delete(`/auth/finance/fee-structures/all/${id}/`);
    return response.data;
  },

  getSalaryStructures: async () => {
    const response = await api.get('/auth/finance/salary-structures/');
    return response.data;
  },

  updateFeeStructure: async (course, data) => {
    const response = await api.patch(`/auth/finance/fee-structures/${course}/update/`, data);
    return response.data;
  },

  getUserBankDetail: async (userId) => {
    const response = await api.get(`/auth/finance/users/${userId}/bank-detail/`);
    return response.data;
  },
    getAllSalaryStructures: async () => {
      const response = await api.get('/auth/finance/salary-structures/all/');
      return response.data;
    },

    createSalaryStructure: async (data) => {
      const response = await api.post('/auth/finance/salary-structures/all/', data);
      return response.data;
    },

    updateSalaryStructure: async (id, data) => {
      const response = await api.patch(`/auth/finance/salary-structures/all/${id}/`, data);
      return response.data;
    },

    deleteSalaryStructure: async (id) => {
      const response = await api.delete(`/auth/finance/salary-structures/all/${id}/`);
      return response.data;
    },
};

export const expenseAPI = {
  getExpenses: async (params = {}) => {
    const response = await api.get('/auth/expenses/', { params });
    return response.data;
  },

  createExpense: async (expenseData) => {
    const isFormData = expenseData instanceof FormData;
    const response = await api.post('/auth/expenses/', expenseData, {
      headers: isFormData ? { 'Content-Type': 'multipart/form-data' } : {},
    });
    return response.data;
  },

  getExpenseDetail: async (expenseId) => {
    const response = await api.get(`/auth/expenses/${expenseId}/`);
    return response.data;
  },

  updateExpense: async (expenseId, data) => {
    const isFormData = data instanceof FormData;
    const response = await api.patch(`/auth/expenses/${expenseId}/`, data, {
      headers: isFormData ? { 'Content-Type': 'multipart/form-data' } : {},
    });
    return response.data;
  },

  deleteExpense: async (expenseId) => {
    const response = await api.delete(`/auth/expenses/${expenseId}/`);
    return response.data;
  },

  getExpenseSummary: async (month = null, year = null) => {
    const params = {};
    if (month) params.month = month;
    if (year) params.year = year;
    const response = await api.get('/auth/expenses/summary/', { params });
    return response.data;
  },

  getCategoryStats: async () => {
    const response = await api.get('/auth/expenses/category-stats/');
    return response.data;
  },
};

// Payroll API
export const payrollAPI = {
  calculatePayroll: async (data) => {
    const response = await api.post('/auth/payroll/calculate/', data);
    return response.data;
  },

  getPayrollRecords: async (params = {}) => {
    const response = await api.get('/auth/payroll/records/', { params });
    return response.data;
  },

  getPayrollDetail: async (payrollId) => {
    const response = await api.get(`/auth/payroll/records/${payrollId}/`);
    return response.data;
  },

  getPayrollSummary: async (month, year) => {
    const response = await api.get('/auth/payroll/summary/', {
      params: { month, year }
    });
    return response.data;
  },

  approvePayroll: async (payrollIds) => {
    const response = await api.post('/auth/payroll/approve/', {
      payroll_ids: payrollIds
    });
    return response.data;
  },

  getLeaveBalances: async (params = {}) => {
    const response = await api.get('/auth/leave/balances/', { params });
    return response.data;
  },

  updateLeaveBalance: async (balanceId, data) => {
    const response = await api.patch(`/auth/leave/balances/${balanceId}/update/`, data);
    return response.data;
  },

  getLeavePolicies: async () => {
    const response = await api.get('/auth/leave/policies/');
    return response.data;
  },
};

export const calendarAPI = {
  getHolidays: async (params = {}) => {
    const response = await api.get('/auth/holidays/', { params });
    return response.data;
  },

  getHolidayDetail: async (id) => {
    const response = await api.get(`/auth/holidays/${id}/`);
    return response.data;
  },

  createHoliday: async (data) => {
    const response = await api.post('/auth/holidays/', data);
    return response.data;
  },

  updateHoliday: async (id, data) => {
    const response = await api.patch(`/auth/holidays/${id}/`, data);
    return response.data;
  },

  deleteHoliday: async (id) => {
    const response = await api.delete(`/auth/holidays/${id}/`);
    return response.data;
  },
};

export const receptionAPI = {
  /** Search existing patients by name / phone / patient_id / Aadhar */
  searchPatient: async (query) => {
    const response = await api.get('/auth/reception/patients/', { params: { q: query } });
    return response.data;
  },

  /** Register a brand-new patient */
  registerPatient: async (patientData) => {
    const response = await api.post('/auth/reception/patients/register/', patientData);
    return response.data;
  },

  getPatientDetail: async (id) => {
    const response = await api.get(`/auth/reception/patients/${id}/`);
    return response.data;
  },

  updatePatient: async (id, data) => {
    const response = await api.patch(`/auth/reception/patients/${id}/`, data);
    return response.data;
  },

  getDoctorsByDepartment: async (departmentId, showUnavailable = false) => {
    const response = await api.get('/auth/reception/doctors/', {
      params: {
        department_id:    departmentId,
        show_unavailable: showUnavailable ? 1 : 0,
      },
    });
    return response.data;
  },

  updateDoctorAvailability: async (doctorId, data) => {
    const response = await api.patch(`/auth/reception/doctors/${doctorId}/availability/`, data);
    return response.data;
  },

  /**
   * Generate an OPD token.
   * @param {{ patient_id, doctor_id, department_id, chief_complaint, notes? }} data
   */
  generateToken: async (data) => {
    const response = await api.post('/auth/reception/tokens/generate/', data);
    return response.data;
  },

  getTokens: async (params = {}) => {
    const response = await api.get('/auth/reception/tokens/', { params });
    return response.data;
  },

  updateTokenStatus: async (tokenId, newStatus) => {
    const response = await api.patch(`/auth/reception/tokens/${tokenId}/status/`, {
      status: newStatus,
    });
    return response.data;
  },

  getQueueOverview: async () => {
    const response = await api.get('/auth/reception/queue-overview/');
    return response.data;
  },

  /**
   * GET /auth/reception/patients/<patient_db_id>/opd-history/
   * Returns all OPD records for a patient (newest first).
   */
  getPatientOPDHistory: async (patientDbId) => {
    const response = await api.get(`/auth/reception/patients/${patientDbId}/opd-history/`);
    return response.data;
  },

  /**
   * PATCH /auth/reception/records/<recordId>/clinical/
   * Save clinical data. Pass is_consultation_complete: true to finalise.
   */
  saveOPDClinical: async (recordId, clinicalData) => {
    const response = await api.patch(
      `/auth/reception/records/${recordId}/clinical/`,
      clinicalData,
    );
    return response.data;
  },

  /**
   * Convenience alias — saves prescription + marks consultation complete.
   */
  saveOPDPrescription: async (recordId, data) => {
    const response = await api.patch(
      `/auth/reception/records/${recordId}/clinical/`,
      { ...data, is_consultation_complete: true },
    );
    return response.data;
  },

  /**
   * GET /auth/reception/records/<recordId>/
   * Returns a single OPDRecord with all clinical fields.
   */
  getOPDRecord: async (recordId) => {
    const response = await api.get(`/auth/reception/records/${recordId}/`);
    return response.data;
  },

  // ─────────────────────────────────────────────────────────────────────────
  // NEW: Department patient search
  // GET /auth/reception/department/patients/?q=...&department_id=...
  //
  // Searches today's OPD records within a department so that any doctor in the
  // same department can look up a patient by name, token number, phone, Aadhar,
  // or chief complaint — even if that patient was not assigned to them.
  // ─────────────────────────────────────────────────────────────────────────
  searchDepartmentPatients: async ({ q = '', department_id = null } = {}) => {
    const response = await api.get('/auth/reception/department/patients/', {
      params: { q, department_id },
    });
    return response.data;
  },

  // ─────────────────────────────────────────────────────────────────────────
  // NEW: Reassign OPD record to a different doctor
  // POST /auth/reception/records/<recordId>/reassign/
  // Body: { doctor_id: number, reason?: string }
  //
  // Transfers full responsibility for the patient to the specified doctor.
  // The new doctor's dashboard will show the patient from this point forward.
  // Can be called by: the currently assigned/original doctor, or a receptionist.
  // ─────────────────────────────────────────────────────────────────────────
  reassignOPDRecord: async (recordId, { doctor_id, reason = '' }) => {
    const response = await api.post(
      `/auth/reception/records/${recordId}/reassign/`,
      { doctor_id, reason },
    );
    return response.data;
  },

  // ─────────────────────────────────────────────────────────────────────────
  // NEW: Fetch doctors for referral picker (wraps existing doctors endpoint)
  // GET /auth/reception/doctors/?department_id=<id>
  //
  // Used by the Referral tab in ConsultationModal to populate:
  //   • "Refer to Specialist"        — same department
  //   • "Refer to Another Department" — any other department
  // ─────────────────────────────────────────────────────────────────────────
  getDoctorsForReferral: async (departmentId) => {
    const response = await api.get('/auth/reception/doctors/', {
      params: { department_id: departmentId, show_unavailable: 0 },
    });
    return response.data;
  },
};

export const doctorOPDAPI = {
  getMyQueue: async (statusFilter = null) => {
    const params = statusFilter ? { status: statusFilter } : {};
    const response = await api.get('/auth/reception/my-queue/', { params });
    return response.data;
  },
  updateTokenStatus: async (tokenId, newStatus) => {
    const response = await api.patch(`/auth/reception/tokens/${tokenId}/status/`, {
      status: newStatus,
    });
    return response.data;
  },
};

// Lab API endpoints
export const labAPI = {
  /** List all active lab tests (for doctor ordering) */
  getTests: async () => {
    const response = await api.get('/auth/lab/tests/');
    return response.data;
  },

  /**
   * List lab orders.
   * @param {{ status?: string }} params  — comma-separated statuses e.g. 'PAID,SAMPLE_COLLECTED'
   */
  getOrders: async (params = {}) => {
    const response = await api.get('/auth/lab/orders/', { params });
    return response.data;
  },

  /**
   * Doctor creates a lab order.
   * @param {{ opd_record_id, test_ids: number[], urgency, special_instructions }} data
   */
  createOrder: async (data) => {
    const response = await api.post('/auth/lab/orders/', data);
    return response.data;
  },

  /** Get full order detail including parameters + existing results */
  getOrder: async (orderId) => {
    const response = await api.get(`/auth/lab/orders/${orderId}/`);
    return response.data;
  },

  /** Finance: generate bill (optionally override bill_amount) */
  generateBill: async (orderId, data = {}) => {
    const response = await api.post(`/auth/lab/orders/${orderId}/generate-bill/`, data);
    return response.data;
  },

  /** Finance: confirm payment */
  confirmPayment: async (orderId, data) => {
    const response = await api.post(`/auth/lab/orders/${orderId}/confirm-payment/`, data);
    return response.data;
  },

  /** Sample collector: record collection + barcode */
  collectSample: async (orderId, data) => {
    const response = await api.post(`/auth/lab/orders/${orderId}/collect-sample/`, data);
    return response.data;
  },

  /** Lab technician: enter/update results */
  enterResults: async (orderId, data) => {
    const response = await api.post(`/auth/lab/orders/${orderId}/enter-results/`, data);
    return response.data;
  },
  // Update a lab test's price (BILLING_MANAGER only)
  updateTestPrice: async (testId, data) => {
    const response = await api.patch(`/auth/lab/tests/${testId}/price/`, data);
    return response.data;
  },

  // Apply discount to a lab order (BILLING_MANAGER only)
  applyDiscount: async (orderId, data) => {
    const response = await api.post(`/auth/lab/orders/${orderId}/apply-discount/`, data);
    return response.data;
  },

  // Get aggregated revenue / stats (BILLING_MANAGER only)
  getBillingStats: async (params = {}) => {
    const response = await api.get('/auth/lab/billing/stats/', { params });
    return response.data;
  },

  // All PAID orders across every category (SAMPLE_COLLECTOR only)
  getSampleCollectionOrders: async (params = {}) => {
    const response = await api.get('/auth/lab/sample-collection/', { params });
    return response.data;
  },

  // Mark a sample as collected without category restriction (SAMPLE_COLLECTOR only)
  collectSampleAsCollector: async (orderId, data) => {
    const response = await api.post(`/auth/lab/sample-collection/${orderId}/collect/`, data);
    return response.data;
  },
  getBillingMembers: async () => {
    const response = await api.get('/auth/users/', { params: { role: 'BILLING_MEMBER' } });
    return response.data;
  },

  // Get orders filtered by biller user ID (BILLING_MANAGER only)
  getOrdersByMember: async (userId, params = {}) => {
    const response = await api.get('/auth/lab/orders/', { params: { billed_by: userId, ...params } });
    return response.data;
  },

  // Get orders filtered by date (BILLING_MANAGER only)
  getOrdersByDate: async (date, params = {}) => {
    // date format: YYYY-MM-DD
    const response = await api.get('/auth/lab/orders/', { params: { order_date: date, ...params } });
    return response.data;
  },
};

export const salarySlipAPI = {

  /**
   * Employee: request a salary slip for a payroll record.
   * POST /auth/payroll/slip-request/
   */
  requestSlip: async (payrollRecordId) => {
    const response = await api.post('/auth/payroll/slip-request/', {
      payroll_record_id: payrollRecordId,
    });
    return response.data;
  },

  /**
   * Employee: list their own slip requests.
   * GET /auth/payroll/slip-requests/mine/
   */
  getMyRequests: async () => {
    const response = await api.get('/auth/payroll/slip-requests/mine/');
    return response.data;
  },

  /**
   * Admin / Principal / Dean: list all slip requests.
   * GET /auth/payroll/slip-requests/all/?status=PENDING
   */
  getAllRequests: async (params = {}) => {
    const response = await api.get('/auth/payroll/slip-requests/all/', { params });
    return response.data;
  },

  /**
   * Get a single slip request by ID.
   * GET /auth/payroll/slip-requests/<pk>/
   */
  getRequest: async (id) => {
    const response = await api.get(`/auth/payroll/slip-requests/${id}/`);
    return response.data;
  },

  /**
   * Admin / Principal / Dean: approve or reject a slip request.
   * POST /auth/payroll/slip-requests/<pk>/approve/
   * @param {number} id
   * @param {{ action: 'approve'|'reject', rejection_reason?: string, notes?: string }} data
   */
  approveOrReject: async (id, data) => {
    const response = await api.post(`/auth/payroll/slip-requests/${id}/approve/`, data);
    return response.data;
  },
};

export const policyAPI = {
  /**
   * GET /auth/admin/policy-configs/?category=PAYROLL
   * Returns all PolicyConfig records, optionally filtered by category.
   * Admin only.
   */
  getAll: async (category = null) => {
    const params = {};
    if (category) params.category = category;
    const response = await api.get('/auth/admin/policy-configs/', { params });
    return response.data;
  },

  /**
   * GET /auth/policy-configs/public/?category=PAYROLL
   * Returns active configs visible to all authenticated users.
   * Used by frontend components that need to read (not write) business rules.
   */
  getPublic: async (category = null) => {
    const params = {};
    if (category) params.category = category;
    const response = await api.get('/auth/policy-configs/public/', { params });
    return response.data;
  },

  /**
   * GET /auth/admin/policy-configs/<id>/
   */
  getById: async (id) => {
    const response = await api.get(`/auth/admin/policy-configs/${id}/`);
    return response.data;
  },

  /**
   * GET /auth/admin/policy-configs/key/<key>/
   */
  getByKey: async (key) => {
    const response = await api.get(`/auth/admin/policy-configs/key/${key}/`);
    return response.data;
  },

  /**
   * PATCH /auth/admin/policy-configs/<id>/
   * Update a single config by PK.
   */
  update: async (id, data) => {
    const response = await api.patch(`/auth/admin/policy-configs/${id}/`, data);
    return response.data;
  },

  /**
   * PATCH /auth/admin/policy-configs/key/<key>/
   * Update a single config by its key string.
   */
  updateByKey: async (key, value) => {
    const response = await api.patch(`/auth/admin/policy-configs/key/${key}/`, { value: String(value) });
    return response.data;
  },

  /**
   * POST /auth/admin/policy-configs/bulk-update/
   * Body: { updates: [{ key: string, value: string }, ...] }
   * Update multiple configs in one request — used by AdminSettings / PayrollRules.
   */
  bulkUpdate: async (updates) => {
    const response = await api.post('/auth/admin/policy-configs/bulk-update/', { updates });
    return response.data;
  },
};

export const mbbsAPI = {

  /**
   * GET /auth/admin/mbbs/students/?year=1&search=<query>
   * Admin: list all MBBS students with year + department info.
   * @param {{ year?: number, search?: string }} params
   */
  getStudents: async (params = {}) => {
    const response = await api.get('/auth/admin/mbbs/students/', { params });
    return response.data;
  },

  /**
   * GET /auth/admin/mbbs/year-map/
   * Returns the year → department mapping for all 4 MBBS years.
   * Used by the Promote Students UI to show subject mapping.
   */
  getYearMap: async () => {
    const response = await api.get('/auth/admin/mbbs/year-map/');
    return response.data;
  },

  /**
   * POST /auth/admin/mbbs/promote/
   * Promote selected MBBS students to the next year.
   * Auto-updates their department assignments.
   *
   * @param {number[]} profileIds  — list of UserProfile.id values
   * @returns {{ promoted_count, skipped_count, promoted, already_max_year }}
   */
  promoteStudents: async (profileIds) => {
    const response = await api.post('/auth/admin/mbbs/promote/', {
      profile_ids: profileIds,
    });
    return response.data;
  },

  /**
   * GET /auth/faculty/mbbs-students/?search=<query>
   * Faculty: list only MBBS students in the faculty's department.
   * Filters are applied server-side based on faculty's assigned department.
   */
  getFacultyMBBSStudents: async (params = {}) => {
    const response = await api.get('/auth/faculty/mbbs-students/', { params });
    return response.data;
  },
};

export const internAPI = {
  /**
   * POST /auth/admin/mbbs/promote-intern/
   * Promote Year-4 MBBS students to Intern role.
   * @param {number[]} profileIds
   * @param {string}   notes
   */
  promoteToIntern: async (profileIds, notes = '') => {
    const response = await api.post('/auth/admin/mbbs/promote-intern/', {
      profile_ids: profileIds,
      notes,
    });
    return response.data;
  },

  /**
   * GET /auth/admin/interns/?status=ACTIVE|COMPLETED
   * List all internship records.
   */
  getAll: async (params = {}) => {
    const response = await api.get('/auth/admin/interns/', { params });
    return response.data;
  },

  /**
   * GET /auth/admin/interns/<id>/
   */
  getById: async (id) => {
    const response = await api.get(`/auth/admin/interns/${id}/`);
    return response.data;
  },

  /**
   * PATCH /auth/admin/interns/<id>/
   * Update internship record (salary, notes, etc.)
   */
  update: async (id, data) => {
    const response = await api.patch(`/auth/admin/interns/${id}/`, data);
    return response.data;
  },

  /**
   * POST /auth/admin/interns/<id>/complete/
   * Mark internship as completed.
   */
  complete: async (id) => {
    const response = await api.post(`/auth/admin/interns/${id}/complete/`);
    return response.data;
  },
};


// ═══════════════════════════════════════════════════════════════════════════
// classAPI — Class scheduling for Principal / HOD / Faculty
// ═══════════════════════════════════════════════════════════════════════════

export const classAPI = {

  // ── Department helpers ──────────────────────────────────────────────────

  /**
   * GET /auth/departments/?year=<n>
   * Returns all departments (used to build the dept code → id map).
   */
  getDeptMap: async () => {
    const response = await api.get('/auth/departments/');
    return response.data;
  },

  /**
   * GET /auth/hod/dept-faculty/
   * Returns faculty members in the HOD's own department (for assignment picker).
   */
  getDeptFaculty: async () => {
    const response = await api.get('/auth/hod/dept-faculty/');
    return response.data;
  },

  // ── Principal endpoints ─────────────────────────────────────────────────

  /**
   * GET /auth/principal/classes/?year=<n>&status=<s>&department=<code>
   */
  getPrincipalClasses: async (params = {}) => {
    const response = await api.get('/auth/principal/classes/', { params });
    return response.data;
  },

  /**
   * POST /auth/principal/classes/
   * @param {{ title, mbbs_year, department, duration_type, start_date, end_date?, schedule_notes? }} data
   */
  createClass: async (data) => {
    const response = await api.post('/auth/principal/classes/', data);
    return response.data;
  },

  /**
   * GET /auth/principal/classes/<id>/
   */
  getClassById: async (id) => {
    const response = await api.get(`/auth/principal/classes/${id}/`);
    return response.data;
  },

  /**
   * PATCH /auth/principal/classes/<id>/
   */
  updateClass: async (id, data) => {
    const response = await api.patch(`/auth/principal/classes/${id}/`, data);
    return response.data;
  },

  /**
   * DELETE /auth/principal/classes/<id>/
   */
  deleteClass: async (id) => {
    const response = await api.delete(`/auth/principal/classes/${id}/`);
    return response.data;
  },

  // ── HOD endpoints ───────────────────────────────────────────────────────

  /**
   * GET /auth/hod/classes/?status=<s>
   * HOD sees classes for their department only.
   */
  getHODClasses: async (params = {}) => {
    const response = await api.get('/auth/hod/classes/', { params });
    return response.data;
  },

  /**
   * GET /auth/hod/classes/stats/
   * Returns { PENDING, ONGOING, COMPLETED, CANCELLED, total }
   */
  getHODStats: async () => {
    const response = await api.get('/auth/hod/classes/stats/');
    return response.data;
  },

  /**
   * POST /auth/hod/classes/<id>/assign/
   * Assign a class to a faculty member.
   * @param {number} classId
   * @param {{ faculty_id: number }} data
   */
  assignClass: async (classId, data) => {
    const response = await api.post(`/auth/hod/classes/${classId}/assign/`, data);
    return response.data;
  },

  // ── Faculty endpoints ───────────────────────────────────────────────────

  /**
   * GET /auth/faculty/classes/?status=<s>
   * Faculty sees only classes assigned to them.
   */
  getFacultyClasses: async (params = {}) => {
    const response = await api.get('/auth/faculty/classes/', { params });
    return response.data;
  },

  /**
   * POST /auth/faculty/classes/<id>/update-status/
   * @param {number} classId
   * @param {{ status: 'PENDING'|'ONGOING'|'COMPLETED'|'CANCELLED', status_note?: string }} data
   */
  updateStatus: async (classId, data) => {
    const response = await api.post(`/auth/faculty/classes/${classId}/update-status/`, data);
    return response.data;
  },

  /**
   * GET /auth/student/class-schedules/?status=<s>
   * Students see class schedules for their MBBS year + crossover classes.
   */
  getStudentClasses: async (params = {}) => {
    const response = await api.get('/auth/student/class-schedules/', { params });
    return response.data;
},
  getStudentMBBSClasses: async (params = {}) => {
    const response = await api.get('/auth/principal/classes/', { params });
    return response.data;
  },
};

// ═══════════════════════════════════════════════════════════════════════════
// timeSlotAPI — Time slot CRUD (Principal / Admin)
// ═══════════════════════════════════════════════════════════════════════════
export const timeSlotAPI = {
  getTimeSlots: async (params = {}) => {
    const response = await api.get('/auth/time-slots/', { params });
    return response.data;
  },

  /**
   * GET /auth/time-slots/?active_only=1
   */
  getAll: async (activeOnly = false) => {
    const params = {};
    if (activeOnly) params.active_only = '1';
    const response = await api.get('/auth/time-slots/', { params });
    return response.data;
  },

  /**
   * POST /auth/time-slots/
   * @param {{ name, start_time, end_time }} data
   */
  create: async (data) => {
    const response = await api.post('/auth/time-slots/', data);
    return response.data;
  },

  /**
   * PATCH /auth/time-slots/<id>/
   */
  update: async (id, data) => {
    const response = await api.patch(`/auth/time-slots/${id}/`, data);
    return response.data;
  },

  /**
   * DELETE /auth/time-slots/<id>/
   * Backend deactivates instead of hard-deleting if classes reference it.
   */
  delete: async (id) => {
    const response = await api.delete(`/auth/time-slots/${id}/`);
    return response.data;
  },
};

// ═══════════════════════════════════════════════════════════════════════════
// classAttendanceAPI — Class schedule attendance tracking
// ═══════════════════════════════════════════════════════════════════════════
export const classAttendanceAPI = {
  /**
   * GET /auth/class-attendance/?class_id=<id>&date=YYYY-MM-DD
   * Faculty / HOD / Principal: list attendance records for a class + optional date.
   */
  getForClass: async (classId, date = null) => {
    const params = { class_id: classId };
    if (date) params.date = date;
    const response = await api.get('/auth/class-attendance/', { params });
    return response.data;
  },

  /**
   * POST /auth/class-attendance/
   * Faculty bulk-marks or updates attendance for a class on a given date.
   * @param {{ class_id, date, records: [{student_id, status, notes}] }} payload
   */
  markBulk: async ({ class_id, date, records }) => {
    const response = await api.post('/auth/class-attendance/', {
      class_id,
      date,
      records,
    });
    return response.data;
  },

  /**
   * POST /auth/class-attendance/self/
   * Student self-marks attendance (pending faculty confirmation).
   * @param {{ class_id, date, status, notes }} payload
   */
  selfMark: async ({ class_id, date, status, notes = '' }) => {
    const response = await api.post('/auth/class-attendance/self/', {
      class_id,
      date,
      status,
      notes,
    });
    return response.data;
  },

  /**
   * GET /auth/class-attendance/mine/?year=2026
   * Student views own attendance records + yearly stats.
   */
  getSubjectClassTypeAnalytics: async (params = {}) => {
    const response = await api.get('/auth/attendance/subject-class-type-analytics/', { params });
    return response.data;
  },

  getMyAttendance: async (params = {}) => {
    const response = await api.get('/auth/class-attendance/mine/', { params });
    return response.data;
  },

  /**
   * GET /auth/class-attendance/summary/?class_id=<id>
   * Per-student attendance summary for a class (Faculty / HOD / Principal).
   */
  getSummary: async (classId) => {
    const response = await api.get('/auth/class-attendance/summary/', {
      params: { class_id: classId },
    });
    return response.data;
  },

  /**
   * POST /auth/class-attendance/confirm/
   * Faculty confirms or rejects self-marked attendance.
   * @param {{ attendance_ids: number[], action: 'confirm'|'reject' }} payload
   */
  confirmSelf: async ({ attendance_ids, action }) => {
    const response = await api.post('/auth/class-attendance/confirm/', {
      attendance_ids,
      action,
    });
    return response.data;
  },
};

// Notification API functions
export const notificationAPI = {
  getNotifications: async () => {
    const response = await api.get('/auth/notifications/');
    return response.data;
  },

  markAsRead: async (notificationId) => {
    const response = await api.post(`/auth/notifications/${notificationId}/read/`);
    return response.data;
  },

  markAllAsRead: async () => {
    const response = await api.post('/auth/notifications/read-all/');
    return response.data;
  },

  getUnreadCount: async () => {
    const response = await api.get('/auth/notifications/unread-count/');
    return response.data;
  },
};

// ── Student search (used during Parent registration) ─────────────────────────
export const studentSearchAPI = {
  /**
   * Search approved students by name or student_id.
   * Works without auth (AllowAny) so parents can search during registration.
   * GET /auth/students/search/?q=<query>
   * @param {string} q  — min 2 characters
   */
  search: async (q) => {
    const response = await api.get('/auth/students/search/', { params: { q } });
    return response.data;
  },
};

// ── Parent API ────────────────────────────────────────────────────────────────
export const parentAPI = {
  /**
   * GET /auth/parent/dashboard/stats/
   * Aggregated stats for all linked students.
   */
  getDashboardStats: async () => {
    const response = await api.get('/auth/parent/dashboard/stats/');
    return response.data;
  },

  /**
   * GET /auth/parent/students/
   * All student links for the authenticated parent.
   */
  getLinkedStudents: async () => {
    const response = await api.get('/auth/parent/students/');
    return response.data;
  },

  /**
   * GET /auth/parent/students/<id>/
   * Full profile for a linked student.
   */
  getStudentDetail: async (studentId) => {
    const response = await api.get(`/auth/parent/students/${studentId}/`);
    return response.data;
  },

  /**
   * GET /auth/parent/students/<id>/attendance/?year=2026
   * Class schedule attendance records for a linked student.
   */
  getAttendance: async (studentId, year = null) => {
    const params = {};
    if (year) params.year = year;
    const response = await api.get(`/auth/parent/students/${studentId}/attendance/`, { params });
    return response.data;
  },

  /**
   * GET /auth/parent/students/<id>/classes/?status=<s>
   * Class schedules for a linked student (MBBS year-based).
   */
  getClasses: async (studentId, params = {}) => {
    const response = await api.get(`/auth/parent/students/${studentId}/classes/`, { params });
    return response.data;
  },

  /**
   * GET /auth/parent/students/<id>/fees/
   * Financial record + recent transactions for a linked student.
   */
  getFees: async (studentId) => {
    const response = await api.get(`/auth/parent/students/${studentId}/fees/`);
    return response.data;
  },

  /**
   * GET /auth/parent/students/<id>/leaves/
   * Leave applications for a linked student.
   */
  getLeaves: async (studentId) => {
    const response = await api.get(`/auth/parent/students/${studentId}/leaves/`);
    return response.data;
  },

  /**
   * GET /auth/parent/students/<id>/announcements/
   * Announcements relevant to a linked student.
   */
  getAnnouncements: async (studentId) => {
    const response = await api.get(`/auth/parent/students/${studentId}/announcements/`);
    return response.data;
  },
};


export const shiftAPI = {
  /** GET /auth/shifts/ — list all defined shifts */
  getShifts: async () => {
    const response = await api.get('/auth/shifts/');
    return response.data;
  },

  /**
   * GET /auth/shifts/users/
   * Returns ALL approved non-student users with their current shift_info
   * and employee_id in a single unpaginated response.
   * Used exclusively by the Shift Management admin tab.
   */
  getShiftUsers: async () => {
    const response = await api.get('/auth/shifts/users/');
    return response.data;
  },

  /** POST /auth/shifts/ — create a new shift */
  createShift: async (data) => {
    const response = await api.post('/auth/shifts/', data);
    return response.data;
  },

  /** PATCH /auth/shifts/<id>/ — update an existing shift */
  updateShift: async (id, data) => {
    const response = await api.patch(`/auth/shifts/${id}/`, data);
    return response.data;
  },

  /** DELETE /auth/shifts/<id>/ — delete shift (fails if users are assigned) */
  deleteShift: async (id) => {
    const response = await api.delete(`/auth/shifts/${id}/`);
    return response.data;
  },

  /**
   * POST /auth/shifts/assign/
   * @param {number}      userId  — target user
   * @param {number|null} shiftId — null to unassign
   */
  assignShift: async (userId, shiftId) => {
    const response = await api.post('/auth/shifts/assign/', {
      user_id:  userId,
      shift_id: shiftId,
    });
    return response.data;
  },

  /**
   * POST /auth/shifts/bulk-assign/
   * @param {number[]}    userIds
   * @param {number|null} shiftId — null to unassign all
   */
  bulkAssignShift: async (userIds, shiftId) => {
    const response = await api.post('/auth/shifts/bulk-assign/', {
      user_ids: userIds,
      shift_id: shiftId,
    });
    return response.data;
  },
};


export const coeAPI = {
  getStats: async () => {
    const response = await api.get('/auth/coe/dashboard/stats/');
    return response.data;
  },
  getPGStudentDepartments: async () => {
    const response = await api.get('/auth/coe/pg-departments/');
    return response.data;
  },
  getTimetables: async (params = {}) => {
    const response = await api.get('/auth/coe/timetables/', { params });
    return response.data;
  },
  createTimetable: async (data) => {
    const response = await api.post('/auth/coe/timetables/', data);
    return response.data;
  },
  getTimetable: async (id) => {
    const response = await api.get(`/auth/coe/timetables/${id}/`);
    return response.data;
  },
  updateTimetable: async (id, data) => {
    const response = await api.patch(`/auth/coe/timetables/${id}/`, data);
    return response.data;
  },
  deleteTimetable: async (id) => {
    const response = await api.delete(`/auth/coe/timetables/${id}/`);
    return response.data;
  },
  getSchedules: async (params = {}) => {
    const response = await api.get('/auth/coe/schedules/', { params });
    return response.data;
  },
  createSchedule: async (data) => {
    const response = await api.post('/auth/coe/schedules/', data);
    return response.data;
  },
  updateSchedule: async (id, data) => {
    const response = await api.patch(`/auth/coe/schedules/${id}/`, data);
    return response.data;
  },
  deleteSchedule: async (id) => {
    const response = await api.delete(`/auth/coe/schedules/${id}/`);
    return response.data;
  },
  getScheduleStudents: async (scheduleId) => {
    const response = await api.get(`/auth/coe/schedules/${scheduleId}/students/`);
    return response.data;
  },
  getEligibleStudents: async (scheduleId, search = '') => {
    const response = await api.get(`/auth/coe/schedules/${scheduleId}/eligible-students/`, {
      params: search ? { search } : {},
    });
    return response.data;
  },
  assignStudents: async (scheduleId, studentIds) => {
    const response = await api.post(`/auth/coe/schedules/${scheduleId}/students/assign/`, {
      student_ids: studentIds,
    });
    return response.data;
  },
  removeAssignment: async (assignmentId) => {
    const response = await api.delete(`/auth/coe/assignments/${assignmentId}/`);
    return response.data;
  },
  updateAttendance: async (scheduleId, records) => {
    const response = await api.post(`/auth/coe/schedules/${scheduleId}/attendance/`, { records });
    return response.data;
  },
  uploadAnswerSheet: async (assignmentId, formData) => {
    const response = await api.post(
      `/auth/coe/assignments/${assignmentId}/upload-sheet/`,
      formData,
      { headers: { 'Content-Type': 'multipart/form-data' } },
    );
    return response.data;
  },
  deleteAnswerSheet: async (assignmentId, sheetId) => {
    const response = await api.delete(
      `/auth/coe/assignments/${assignmentId}/upload-sheet/?sheet_id=${sheetId}`,
    );
    return response.data;
  },
  generateEvalFile: async (assignmentId) => {
    const response = await api.post(`/auth/coe/assignments/${assignmentId}/generate-eval/`);
    return response.data;
  },
  // Returns the proxy URL string (no fetch — just builds the URL for PDF.js)
  proxyPdfUrl: (assignmentId) => {
    const base = (process.env.REACT_APP_API_URL || 'http://localhost:8000/api');
    return `${base}/auth/coe/proxy-pdf/${assignmentId}/`;
  },
  getQuestionPapers: async () => {
    const response = await api.get('/auth/coe/question-papers/');
    return response.data;
  },
  createQuestionPaper: async (data) => {
    const response = await api.post('/auth/coe/question-papers/', data);
    return response.data;
  },
  updateQuestionPaper: async (id, data) => {
    const response = await api.patch(`/auth/coe/question-papers/${id}/`, data);
    return response.data;
  },
  deleteQuestionPaper: async (id) => {
    const response = await api.delete(`/auth/coe/question-papers/${id}/`);
    return response.data;
  },
  addSection: async (paperId, data) => {
    const response = await api.post(`/auth/coe/question-papers/${paperId}/sections/`, data);
    return response.data;
  },
  addQuestion: async (sectionId, data) => {
    const response = await api.post(`/auth/coe/sections/${sectionId}/questions/`, data);
    return response.data;
  },
  getMyExaminations: async () => {
    const response = await api.get('/auth/student/my-examinations/');
    return response.data;
  },
  getAllStudentAssignments: async (params = {}) => {
    const response = await api.get('/auth/coe/all-student-assignments/', { params });
    return response.data;
  },
  // Thesis Evaluation (COE)
  getThesis: async (params = {}) => {
    const response = await api.get('/auth/coe/thesis/', { params });
    return response.data;
  },
  evaluateThesis: async (id, data) => {
    const response = await api.post(`/auth/coe/thesis/${id}/evaluate/`, data);
    return response.data;
  },
  // Paper Evaluator APIs
  getPaperEvaluatorApprovals: async () => {
    const response = await api.get('/auth/coe/paper-evaluator-approvals/');
    return response.data;
  },
  approveRejectPaperEvaluator: async (id, action, reason = '') => {
    const response = await api.post(`/auth/coe/paper-evaluator-approvals/${id}/action/`, {
      action,
      rejection_reason: reason
    });
    return response.data;
  },
  getPaperEvaluators: async () => {
    const response = await api.get('/auth/coe/paper-evaluators/');
    return response.data;
  },
  getBundles: async () => {
    const response = await api.get('/auth/coe/evaluator-bundles/');
    return response.data;
  },
  createBundle: async (scheduleId, questionPaperId, studentAssignmentIds) => {
    const response = await api.post('/auth/coe/evaluator-bundles/', {
      schedule_id: scheduleId,
      question_paper_id: questionPaperId,
      student_assignment_ids: studentAssignmentIds
    });
    return response.data;
  },
  assignBundleEvaluators: async (bundleId, evaluatorIds) => {
    const response = await api.post(`/auth/coe/evaluator-bundles/${bundleId}/assign-evaluators/`, {
      evaluator_ids: evaluatorIds
    });
    return response.data;
  },
  deleteBundle: async (id) => {
    const response = await api.delete(`/auth/coe/evaluator-bundles/${id}/`);
    return response.data;
  },
  reassignStudent: async (studentAssignmentId, action, targetBundleId = null) => {
    const response = await api.post('/auth/coe/evaluator-bundles/student-reassign/', {
      student_assignment_id: studentAssignmentId,
      action,
      target_bundle_id: targetBundleId
    });
    return response.data;
  },
  assignBundleEvaluatorsV2: async (bundleId, evaluatorIds) => {
    const response = await api.post(`/auth/coe/evaluator-bundles/${bundleId}/assign-evaluators-v2/`, {
      evaluator_ids: evaluatorIds
    });
    return response.data;
  },
};

export const studentBiometricAPI = {
  /**
   * GET /auth/student/biometric-logs/
   * Student's own raw punch logs.
   * Params: date, month, year, result
   */
  getLogs: async (params = {}) => {
    const response = await api.get('/auth/student/biometric-logs/', { params });
    return response.data;
  },

  /**
   * GET /auth/admin/student-biometric-logs/
   * Admin: any student's logs.
   * Params: user_id, date, month, year, result
   */
  getAdminLogs: async (params = {}) => {
    const response = await api.get('/auth/admin/student-biometric-logs/', { params });
    return response.data;
  },

  /**
   * GET /auth/student/attendance/biometric-summary/?date=YYYY-MM-DD
   * Daily class attendance summary.
   * Params: date, user_id (admin only)
   */
  getDailySummary: async (date = null, userId = null) => {
    const params = {};
    if (date)   params.date    = date;
    if (userId) params.user_id = userId;
    const response = await api.get('/auth/student/attendance/biometric-summary/', { params });
    return response.data;
  },

  /**
   * POST /auth/admin/trigger-absent-sweep/
   * Admin: manually run absent sweep.
   * Body: { date?: 'YYYY-MM-DD' }
   */
  triggerAbsentSweep: async (date = null) => {
    const body = {};
    if (date) body.date = date;
    const response = await api.post('/auth/admin/trigger-absent-sweep/', body);
    return response.data;
  },
};


export const grievanceAPI = {
  // Any user: submit (FormData for optional file attachment)
  submit: async (formData) => {
    const response = await api.post('/auth/grievances/submit/', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data;
  },

  // All grievances — role-filtered on the backend
  getAll: async (params = {}) => {
    const response = await api.get('/auth/grievances/', { params });
    return response.data;
  },

  // Current user's own submissions
  getMine: async (params = {}) => {
    const response = await api.get('/auth/grievances/mine/', { params });
    return response.data;
  },

  // Dept staff: grievances assigned to their role / to them
  getDepartmentGrievances: async (params = {}) => {
    const response = await api.get('/auth/grievances/department/', { params });
    return response.data;
  },

  // Full detail (with responses, timeline, escalations)
  getDetail: async (id) => {
    const response = await api.get(`/auth/grievances/${id}/`);
    return response.data;
  },

  // Officer/Admin: assign to a department role
  assign: async (id, payload) => {
    const response = await api.post(`/auth/grievances/${id}/assign/`, payload);
    return response.data;
  },

  // Dept staff: UNDER_REVIEW / RESOLVED / REJECTED
  updateStatus: async (id, payload) => {
    const response = await api.post(`/auth/grievances/${id}/update-status/`, payload);
    return response.data;
  },

  // Officer/Admin: escalate to higher authority
  escalate: async (id, payload) => {
    const response = await api.post(`/auth/grievances/${id}/escalate/`, payload);
    return response.data;
  },

  // Officer/Admin: close or reopen
  closeOrReopen: async (id, payload) => {
    const response = await api.post(`/auth/grievances/${id}/close-reopen/`, payload);
    return response.data;
  },

  // Any involved party: add a comment/response
  respond: async (id, formData) => {
    const response = await api.post(`/auth/grievances/${id}/respond/`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data;
  },

  // Officer/Admin: dashboard statistics
  getOfficerStats: async () => {
    const response = await api.get('/auth/grievances/officer/stats/');
    return response.data;
  },

  // Officer/Admin: list users for a role (for assignment dropdown)
  getUsersByRole: async (role) => {
    const response = await api.get('/auth/grievances/users-by-role/', { params: { role } });
    return response.data;
  },
  getDepartments: async () => {
    let allDepartments = [];
    let nextUrl = '/auth/departments/';

    while (nextUrl) {
      const response = await api.get(nextUrl);
      const data = response.data;

      if (data.results && Array.isArray(data.results)) {
        allDepartments = [...allDepartments, ...data.results];
        nextUrl = data.next
          ? new URL(data.next).pathname + new URL(data.next).search
          : null;
      } else if (Array.isArray(data)) {
        return data;
      } else {
        break;
      }
    }

    return allDepartments;
  },

  // Reuses /auth/departments/<id>/faculty/ — lists faculty for a given dept
  getFacultyByDepartment: async (departmentId) => {
    let allFaculty = [];
    let nextUrl = `/auth/departments/${departmentId}/faculty/`;

    while (nextUrl) {
      const response = await api.get(nextUrl);
      const data = response.data;

      if (data.results && Array.isArray(data.results)) {
        allFaculty = [...allFaculty, ...data.results];
        nextUrl = data.next
          ? new URL(data.next).pathname + new URL(data.next).search
          : null;
      } else if (Array.isArray(data)) {
        return data;
      } else {
        break;
      }
    }

    return allFaculty;
  },
};

// Helper functions for token management
export const tokenManager = {
  setTokens: (accessToken, refreshToken) => {
    localStorage.setItem('access_token', accessToken);
    localStorage.setItem('refresh_token', refreshToken);
  },

  clearTokens: () => {
    localStorage.removeItem('access_token');
    localStorage.removeItem('refresh_token');
    localStorage.removeItem('user');
  },

  getAccessToken: () => {
    return localStorage.getItem('access_token');
  },

  getRefreshToken: () => {
    return localStorage.getItem('refresh_token');
  },

  isAuthenticated: () => {
    return !!localStorage.getItem('access_token');
  },
};

export const studentThesisAPI = {
  getMyThesis: async () => {
    const response = await api.get('/auth/student/thesis/');
    return response.data;
  },
  createThesis: async (data) => {
    const response = await api.post('/auth/student/thesis/', data);
    return response.data;
  },
  patchThesis: async (data) => {
    const response = await api.patch('/auth/student/thesis/', data);
    return response.data;
  },
  uploadDocument: async (submissionId, formData) => {
    const response = await api.post(`/auth/student/thesis/${submissionId}/upload/`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data;
  },
  deleteDocument: async (submissionId, docId) => {
    const response = await api.delete(`/auth/student/thesis/${submissionId}/documents/${docId}/`);
    return response.data;
  },
};

export const paperEvaluatorAPI = {
  getAssignments: async () => {
    const response = await api.get('/auth/evaluator/my-assignments/');
    return response.data;
  },
  getEvaluation: async (id) => {
    const response = await api.get(`/auth/evaluator/evaluations/${id}/`);
    return response.data;
  },
  updateEvaluation: async (id, data) => {
    const response = await api.patch(`/auth/evaluator/evaluations/${id}/`, data);
    return response.data;
  },
  uploadVideo: async (id, formData) => {
    const response = await api.post(`/auth/evaluator/evaluations/${id}/upload-video/`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data;
  },
};

export const adminCMPermissionAPI = {
  getCMFaculty: async () => {
    const response = await api.get('/auth/admin/community-medicine/faculty/');
    return response.data;
  },
  grantCMPermission: async (facultyId) => {
    const response = await api.post(`/auth/admin/community-medicine/faculty/${facultyId}/grant/`);
    return response.data;
  },
  revokeCMPermission: async (facultyId) => {
    const response = await api.delete(`/auth/admin/community-medicine/faculty/${facultyId}/grant/`);
    return response.data;
  },
};

export const studentThesisProposalAPI = {
  getProposal: async () => {
    const response = await api.get('/auth/student/thesis-proposal/');
    return response.data;
  },
  submitProposal: async (formData) => {
    const response = await api.post('/auth/student/thesis-proposal/', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data;
  },
  updateProposal: async (formData) => {
    const response = await api.patch('/auth/student/thesis-proposal/', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data;
  },
};

export const facultyThesisProposalAPI = {
  getProposals: async (status = 'PENDING') => {
    const response = await api.get('/auth/faculty/thesis-proposals/', { params: { status } });
    return response.data;
  },
  reviewProposal: async (id, action, remarks) => {
    const response = await api.post(`/auth/faculty/thesis-proposals/${id}/review/`, { action, remarks });
    return response.data;
  },
  checkCMPermission: async () => {
    const response = await api.get('/auth/faculty/community-medicine-permission/');
    return response.data;
  },
};

export const studentEthicsAPI = {
  getSubmissions: async () => {
    const response = await api.get('/auth/student/ethics/');
    return response.data;
  },
  submitEthics: async (formData) => {
    const response = await api.post('/auth/student/ethics/', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data;
  },
  getEthicsDetail: async (id) => {
    const response = await api.get(`/auth/student/ethics/${id}/`);
    return response.data;
  },
  updateEthics: async (id, formData) => {
    const response = await api.patch(`/auth/student/ethics/${id}/`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data;
  },
  uploadFinalPdf: async (id, formData) => {
    const response = await api.post(`/auth/student/ethics/${id}/upload/`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data;
  },
};

export const facultyEthicsAPI = {
  getSubmissions: async (status = 'PENDING') => {
    const response = await api.get('/auth/faculty/ethics/', { params: { status } });
    return response.data;
  },
  reviewSubmission: async (id, action, remarks) => {
    const response = await api.post(`/auth/faculty/ethics/${id}/review/`, { action, remarks });
    return response.data;
  },
};

export const coeEthicsAPI = {
  getSubmissions: async (search = '') => {
    const response = await api.get('/auth/coe/ethics/', { params: search ? { search } : {} });
    return response.data;
  },
  evaluateSubmission: async (id, data) => {
    const response = await api.post(`/auth/coe/ethics/${id}/evaluate/`, data);
    return response.data;
  },
  getThesisProposals: async () => {
    const response = await api.get('/auth/coe/thesis-proposals/');
    return response.data;
  },
};

export const hodExamAPI = {
  getSchedules: async () => {
    const response = await api.get('/auth/hod/exam-schedules/');
    return response.data;
  },
  getAssignments: async (scheduleId) => {
    const response = await api.get(`/auth/hod/exam-schedules/${scheduleId}/assignments/`);
    return response.data;
  },
  submitMarks: async (scheduleId, records) => {
    const response = await api.post(`/auth/hod/exam-schedules/${scheduleId}/assignments/marks/`, { records });
    return response.data;
  },
};

// ══════════════════════════════════════════════════════════════════════════════
// LIBRARY MANAGEMENT API
// ══════════════════════════════════════════════════════════════════════════════

export const libraryAPI = {
  // Dashboard
  getStats: async () => {
    const r = await api.get('/auth/library/stats/');
    return r.data;
  },

  // Settings
  getSettings: async () => {
    const r = await api.get('/auth/library/settings/');
    return r.data;
  },
  saveSettings: async (items) => {
    const r = await api.post('/auth/library/settings/', items);
    return r.data;
  },

  // Categories
  getCategories: async (params = {}) => {
    const r = await api.get('/auth/library/categories/', { params });
    return r.data;
  },
  createCategory: async (data) => {
    const r = await api.post('/auth/library/categories/', data);
    return r.data;
  },

  // Books
  getBooks: async (params = {}) => {
    const r = await api.get('/auth/library/books/', { params });
    return r.data;
  },
  getBook: async (id) => {
    const r = await api.get(`/auth/library/books/${id}/`);
    return r.data;
  },
  createBook: async (data) => {
    const isForm = data instanceof FormData;
    const r = await api.post('/auth/library/books/', data, isForm ? { headers: { 'Content-Type': 'multipart/form-data' } } : {});
    return r.data;
  },
  updateBook: async (id, data) => {
    const r = await api.patch(`/auth/library/books/${id}/`, data);
    return r.data;
  },
  archiveBook: async (id) => {
    const r = await api.delete(`/auth/library/books/${id}/`);
    return r.data;
  },

  // Copies
  getCopies: async (bookId) => {
    const r = await api.get(`/auth/library/books/${bookId}/copies/`);
    return r.data;
  },
  createCopy: async (bookId, data) => {
    const r = await api.post(`/auth/library/books/${bookId}/copies/`, data);
    return r.data;
  },
  updateCopy: async (copyId, data) => {
    const r = await api.patch(`/auth/library/copies/${copyId}/`, data);
    return r.data;
  },

  // Members
  getMembers: async (params = {}) => {
    const r = await api.get('/auth/library/members/', { params });
    return r.data;
  },
  getMember: async (id) => {
    const r = await api.get(`/auth/library/members/${id}/`);
    return r.data;
  },
  updateMember: async (id, data) => {
    const r = await api.patch(`/auth/library/members/${id}/`, data);
    return r.data;
  },
  getMyMembership: async () => {
    const r = await api.get('/auth/library/my-membership/');
    return r.data;
  },
  createMyMembership: async () => {
    const r = await api.post('/auth/library/my-membership/');
    return r.data;
  },

  // Issues
  getIssues: async (params = {}) => {
    const r = await api.get('/auth/library/issues/', { params });
    return r.data;
  },
  issueBook: async (data) => {
    const r = await api.post('/auth/library/issues/', data);
    return r.data;
  },
  returnBook: async (issueId, data) => {
    const r = await api.post(`/auth/library/issues/${issueId}/return/`, data);
    return r.data;
  },
  getMyIssues: async () => {
    const r = await api.get('/auth/library/my-issues/');
    return r.data;
  },
  getOverdue: async () => {
    const r = await api.get('/auth/library/overdue/');
    return r.data;
  },

  // Fines
  getFines: async (params = {}) => {
    const r = await api.get('/auth/library/fines/', { params });
    return r.data;
  },
  payFine: async (fineId, data) => {
    const r = await api.post(`/auth/library/fines/${fineId}/pay/`, data);
    return r.data;
  },
  waiveFine: async (fineId, data) => {
    const r = await api.post(`/auth/library/fines/${fineId}/waive/`, data);
    return r.data;
  },
  getMyFines: async () => {
    const r = await api.get('/auth/library/my-fines/');
    return r.data;
  },

  // Reservations
  getReservations: async (params = {}) => {
    const r = await api.get('/auth/library/reservations/', { params });
    return r.data;
  },
  createReservation: async (bookId) => {
    const r = await api.post('/auth/library/reservations/', { book: bookId });
    return r.data;
  },
  cancelReservation: async (id) => {
    const r = await api.post(`/auth/library/reservations/${id}/cancel/`);
    return r.data;
  },

  // Digital Resources
  getDigitalResources: async (params = {}) => {
    const r = await api.get('/auth/library/digital/', { params });
    return r.data;
  },
  uploadDigitalResource: async (formData) => {
    const r = await api.post('/auth/library/digital/', formData, { headers: { 'Content-Type': 'multipart/form-data' } });
    return r.data;
  },
  deleteDigitalResource: async (id) => {
    const r = await api.delete(`/auth/library/digital/${id}/`);
    return r.data;
  },

  // Journals
  getJournals: async (params = {}) => {
    const r = await api.get('/auth/library/journals/', { params });
    return r.data;
  },
  createJournal: async (data) => {
    const r = await api.post('/auth/library/journals/', data);
    return r.data;
  },
  updateJournal: async (id, data) => {
    const r = await api.patch(`/auth/library/journals/${id}/`, data);
    return r.data;
  },
  deleteJournal: async (id) => {
    const r = await api.delete(`/auth/library/journals/${id}/`);
    return r.data;
  },

  // Barcodes
  getNextBarcodes: async (count = 1) => {
    const r = await api.get('/auth/library/barcodes/next/', { params: { count } });
    return r.data;
  },

  // Bulk copy creation with custom barcodes
  createBulkCopies: async (bookId, barcodes) => {
    const r = await api.post(`/auth/library/books/${bookId}/copies/`, { barcodes });
    return r.data;
  },


  // Reports
  getReports: async (params = {}) => {
    const r = await api.get('/auth/library/reports/', { params });
    return r.data;
  },
  getInventoryReport: async () => {
    const r = await api.get('/auth/library/reports/', { params: { type: 'inventory' } });
    return r.data;
  },
  getOverdueReport: async () => {
    const r = await api.get('/auth/library/reports/', { params: { type: 'overdue' } });
    return r.data;
  },
  getFinesReport: async (status = '') => {
    const r = await api.get('/auth/library/reports/', { params: { type: 'fines', status } });
    return r.data;
  },
};

// ============================================================
// HOSTEL & INVENTORY API
// ============================================================
export const hostelInventoryAPI = {
  // ── Hostel ──────────────────────────────────────────────────────────────
  getHostelStats: () => api.get('/auth/hostel/stats/').then(r => r.data),
  getHostels: (params = {}) => api.get('/auth/hostel/hostels/', { params }).then(r => r.data),
  createHostel: (data) => api.post('/auth/hostel/hostels/', data).then(r => r.data),
  updateHostel: (id, data) => api.patch(`/auth/hostel/hostels/${id}/`, data).then(r => r.data),
  deleteHostel: (id) => api.delete(`/auth/hostel/hostels/${id}/`).then(r => r.data),

  getBuildings: (params = {}) => api.get('/auth/hostel/buildings/', { params }).then(r => r.data),
  createBuilding: (data) => api.post('/auth/hostel/buildings/', data).then(r => r.data),

  getFloors: (params = {}) => api.get('/auth/hostel/floors/', { params }).then(r => r.data),
  createFloor: (data) => api.post('/auth/hostel/floors/', data).then(r => r.data),

  getRooms: (params = {}) => api.get('/auth/hostel/rooms/', { params }).then(r => r.data),
  createRoom: (data) => api.post('/auth/hostel/rooms/', data).then(r => r.data),
  updateRoom: (id, data) => api.patch(`/auth/hostel/rooms/${id}/`, data).then(r => r.data),

  getAllocations: (params = {}) => api.get('/auth/hostel/allocations/', { params }).then(r => r.data),
  createAllocation: (data) => api.post('/auth/hostel/allocations/', data).then(r => r.data),
  updateAllocation: (id, data) => api.patch(`/auth/hostel/allocations/${id}/`, data).then(r => r.data),

  getAttendance: (params = {}) => api.get('/auth/hostel/attendance/', { params }).then(r => r.data),
  markAttendance: (data) => api.post('/auth/hostel/attendance/', data).then(r => r.data),

  getVisitors: (params = {}) => api.get('/auth/hostel/visitors/', { params }).then(r => r.data),
  addVisitor: (data) => api.post('/auth/hostel/visitors/', data).then(r => r.data),

  getLeaves: (params = {}) => api.get('/auth/hostel/leaves/', { params }).then(r => r.data),
  applyLeave: (data) => api.post('/auth/hostel/leaves/', data).then(r => r.data),
  approveLeave: (id, data) => api.post(`/auth/hostel/leaves/${id}/approve/`, data).then(r => r.data),

  getComplaints: (params = {}) => api.get('/auth/hostel/complaints/', { params }).then(r => r.data),
  submitComplaint: (data) => {
    const config = data instanceof FormData ? { headers: { 'Content-Type': 'multipart/form-data' } } : {};
    return api.post('/auth/hostel/complaints/', data, config).then(r => r.data);
  },
  updateComplaint: (id, data) => api.patch(`/auth/hostel/complaints/${id}/`, data).then(r => r.data),

  getFees: (params = {}) => api.get('/auth/hostel/fees/', { params }).then(r => r.data),
  createFee: (data) => api.post('/auth/hostel/fees/', data).then(r => r.data),
  updateFee: (id, data) => api.patch(`/auth/hostel/fees/${id}/`, data).then(r => r.data),

  getSettings: () => api.get('/auth/hostel/settings/').then(r => r.data),
  updateSettings: (data) => api.patch('/auth/hostel/settings/', data).then(r => r.data),
  getMyHostelProfile: () => api.get('/auth/hostel/my-profile/').then(r => r.data),
  searchResidents: (search, excludeAllocated = false, allocatedOnly = false, hostelId = null) => api.get('/auth/hostel/search-residents/', { params: { search, exclude_allocated: excludeAllocated, allocated_only: allocatedOnly, hostel_id: hostelId } }).then(r => r.data),
  getOutpasses: (params = {}) => api.get('/auth/hostel/outpasses/', { params }).then(r => r.data),
  createOutpass: (data) => api.post('/auth/hostel/outpasses/', data).then(r => r.data),
  closeOutpass: (id) => api.post(`/auth/hostel/outpasses/${id}/close/`).then(r => r.data),

  // ── Room Accessories / Assets / Events ──────────────────────────────────
  getRoomDetail: (roomId) => api.get(`/auth/hostel/rooms/${roomId}/detail/`).then(r => r.data),
  getRoomAccessories: (roomId) => api.get(`/auth/hostel/rooms/${roomId}/accessories/`).then(r => r.data),
  addRoomAccessory: (roomId, data) => api.post(`/auth/hostel/rooms/${roomId}/accessories/`, data).then(r => r.data),
  getRoomAssets: (roomId, params = {}) => api.get(`/auth/hostel/rooms/${roomId}/assets/`, { params }).then(r => r.data),
  getGlobalAssets: (params = {}) => api.get('/auth/hostel/assets/', { params }).then(r => r.data),
  getAssetDetail: (assetId) => api.get(`/auth/hostel/assets/${assetId}/`).then(r => r.data),
  updateAssetStatus: (assetId, data) => api.patch(`/auth/hostel/assets/${assetId}/`, data).then(r => r.data),
  getAssetEvents: (assetId) => api.get(`/auth/hostel/assets/${assetId}/events/`).then(r => r.data),
  addAssetEvent: (assetId, data) => api.post(`/auth/hostel/assets/${assetId}/events/`, data).then(r => r.data),

  // ── Complaint Workflow Actions ───────────────────────────────────────────
  markComplaintDamaged: (id, data = {}) => api.post(`/auth/hostel/complaints/${id}/mark-damaged/`, data).then(r => r.data),
  startComplaintRepair: (id, data = {}) => api.post(`/auth/hostel/complaints/${id}/start-repair/`, data).then(r => r.data),
  completeComplaintRepair: (id, data = {}) => api.post(`/auth/hostel/complaints/${id}/complete-repair/`, data).then(r => r.data),
  requestComplaintReplacement: (id, data = {}) => api.post(`/auth/hostel/complaints/${id}/request-replacement/`, data).then(r => r.data),

  // ── Hostel Purchase Requests ─────────────────────────────────────────────
  getHostelPurchaseRequests: (params = {}) => api.get('/auth/hostel/purchase-requests/', { params }).then(r => r.data),
  createHostelPurchaseRequest: (data) => api.post('/auth/hostel/purchase-requests/', data).then(r => r.data),
  getHostelPurchaseRequest: (id) => api.get(`/auth/hostel/purchase-requests/${id}/`).then(r => r.data),
  updateHostelPurchaseRequest: (id, data) => api.patch(`/auth/hostel/purchase-requests/${id}/`, data).then(r => r.data),
  installHostelAsset: (id, data) => api.post(`/auth/hostel/purchase-requests/${id}/install/`, data).then(r => r.data),


  getInventoryStats: () => api.get('/auth/inventory/stats/').then(r => r.data),

  getCategories: () => api.get('/auth/inventory/categories/').then(r => r.data),
  createCategory: (data) => api.post('/auth/inventory/categories/', data).then(r => r.data),

  getItems: (params = {}) => api.get('/auth/inventory/items/', { params }).then(r => r.data),
  createItem: (formData) => api.post('/auth/inventory/items/', formData, { headers: { 'Content-Type': 'multipart/form-data' } }).then(r => r.data),
  updateItem: (id, data) => api.patch(`/auth/inventory/items/${id}/`, data).then(r => r.data),
  deleteItem: (id) => api.delete(`/auth/inventory/items/${id}/`).then(r => r.data),

  getAssignments: (params = {}) => api.get('/auth/inventory/assignments/', { params }).then(r => r.data),
  createAssignment: (data) => api.post('/auth/inventory/assignments/', data).then(r => r.data),
  returnAsset: (id, data) => api.patch(`/auth/inventory/assignments/${id}/`, data).then(r => r.data),

  getMaintenance: (params = {}) => api.get('/auth/inventory/maintenance/', { params }).then(r => r.data),
  createMaintenance: (data) => api.post('/auth/inventory/maintenance/', data).then(r => r.data),
  updateMaintenance: (id, data) => api.patch(`/auth/inventory/maintenance/${id}/`, data).then(r => r.data),

  getVendors: (params = {}) => api.get('/auth/inventory/vendors/', { params }).then(r => r.data),
  createVendor: (data) => api.post('/auth/inventory/vendors/', data).then(r => r.data),
  updateVendor: (id, data) => api.patch(`/auth/inventory/vendors/${id}/`, data).then(r => r.data),

  getPurchaseRequests: (params = {}) => api.get('/auth/inventory/purchase-requests/', { params }).then(r => r.data),
  createPurchaseRequest: (data) => api.post('/auth/inventory/purchase-requests/', data).then(r => r.data),
  approvePurchaseRequest: (id, data) => api.post(`/auth/inventory/purchase-requests/${id}/approve/`, data).then(r => r.data),

  getPurchaseOrders: (params = {}) => api.get('/auth/inventory/purchase-orders/', { params }).then(r => r.data),
  createPurchaseOrder: (data) => api.post('/auth/inventory/purchase-orders/', data).then(r => r.data),
  updatePurchaseOrder: (id, data) => api.patch(`/auth/inventory/purchase-orders/${id}/`, data).then(r => r.data),

  getStockTransactions: (params = {}) => api.get('/auth/inventory/stock-transactions/', { params }).then(r => r.data),
  addStockTransaction: (data) => api.post('/auth/inventory/stock-transactions/', data).then(r => r.data),

  getConsumables: (params = {}) => api.get('/auth/inventory/consumables/', { params }).then(r => r.data),
  getConsumableAlerts: () => api.get('/auth/inventory/consumables/alerts/').then(r => r.data),

  getDeptStock: (params = {}) => api.get('/auth/inventory/department-stock/', { params }).then(r => r.data),
  getAudits: () => api.get('/auth/inventory/audits/').then(r => r.data),
  createAudit: (data) => api.post('/auth/inventory/audits/', data).then(r => r.data),
};

/**
 * OTP API service for email verification during registration.
 */
export const otpAPI = {
  /**
   * Send OTP to the given email address.
   * POST /auth/otp/send/
   */
  sendOTP: async (email) => {
    const response = await api.post("/auth/otp/send/", { email });
    return response.data;
  },

  /**
   * Verify OTP entered by the user.
   * POST /auth/otp/verify/
   */
  verifyOTP: async (email, otp) => {
    const response = await api.post("/auth/otp/verify/", { email, otp });
    return response.data;
  },
};

/**
 * Vehicle / Fleet Management API service
 */
export const transportAPI = {
  getStats: async () => {
    const res = await api.get('/auth/transport/stats/');
    return res.data;
  },
  getVehicles: async (params = {}) => {
    const res = await api.get('/auth/transport/vehicles/', { params });
    return res.data;
  },
  getVehicleDetail: async (id) => {
    const res = await api.get(`/auth/transport/vehicles/${id}/`);
    return res.data;
  },
  createVehicle: async (data) => {
    const isFormData = data instanceof FormData;
    const config = isFormData ? { headers: { 'Content-Type': 'multipart/form-data' } } : {};
    const res = await api.post('/auth/transport/vehicles/', data, config);
    return res.data;
  },
  updateVehicle: async (id, data) => {
    const isFormData = data instanceof FormData;
    const config = isFormData ? { headers: { 'Content-Type': 'multipart/form-data' } } : {};
    const res = await api.patch(`/auth/transport/vehicles/${id}/`, data, config);
    return res.data;
  },
  deleteVehicle: async (id) => {
    const res = await api.delete(`/auth/transport/vehicles/${id}/`);
    return res.data;
  },

  // ── Step 3: Fee Configuration & Route Management ──────────────────────────
  getAvailableCourses: async () => {
    const res = await api.get('/auth/transport/fee-config/courses/');
    return res.data;
  },
  getFeeConfig: async () => {
    const res = await api.get('/auth/transport/fee-config/');
    return res.data;
  },
  selectFeeMethod: async (activeFeeMethod) => {
    const res = await api.post('/auth/transport/fee-config/select-method/', { active_fee_method: activeFeeMethod });
    return res.data;
  },
  getGlobalCourseFees: async () => {
    const res = await api.get('/auth/transport/fee-config/global-fees/');
    return res.data;
  },
  saveGlobalCourseFees: async (fees) => {
    const res = await api.post('/auth/transport/fee-config/global-fees/', { fees });
    return res.data;
  },
  getDistanceSlabs: async () => {
    const res = await api.get('/auth/transport/fee-config/slabs/');
    return res.data;
  },
  createDistanceSlab: async (data) => {
    const res = await api.post('/auth/transport/fee-config/slabs/', data);
    return res.data;
  },
  updateDistanceSlab: async (id, data) => {
    const res = await api.patch(`/auth/transport/fee-config/slabs/${id}/`, data);
    return res.data;
  },
  deleteDistanceSlab: async (id) => {
    const res = await api.delete(`/auth/transport/fee-config/slabs/${id}/`);
    return res.data;
  },
  finalizeDistanceSlab: async (id) => {
    const res = await api.post(`/auth/transport/fee-config/slabs/${id}/finalize/`);
    return res.data;
  },
  getRoutes: async () => {
    const res = await api.get('/auth/transport/routes/');
    return res.data;
  },
  createRoute: async (data) => {
    const res = await api.post('/auth/transport/routes/', data);
    return res.data;
  },
  updateRoute: async (id, data) => {
    const res = await api.patch(`/auth/transport/routes/${id}/`, data);
    return res.data;
  },
  deleteRoute: async (id) => {
    const res = await api.delete(`/auth/transport/routes/${id}/`);
    return res.data;
  },

  // ── Step 4: Route Vehicle Assignment ───────────────────────────────────────
  getEligibleVehiclesForRoute: async (routeId) => {
    const res = await api.get(`/auth/transport/routes/${routeId}/eligible-vehicles/`);
    return res.data;
  },
  assignVehicleToRoute: async (routeId, vehicleId) => {
    const res = await api.post(`/auth/transport/routes/${routeId}/assign-vehicle/`, { vehicle_id: vehicleId });
    return res.data;
  },
  unassignVehicleFromRoute: async (routeId) => {
    const res = await api.post(`/auth/transport/routes/${routeId}/unassign-vehicle/`);
    return res.data;
  },

  // ── Step 4: Student Transport Assignment ──────────────────────────────────
  searchStudentsForTransport: async (query = '') => {
    const res = await api.get('/auth/transport/students/search/', { params: { q: query } });
    return res.data;
  },
  calculateStudentTransportFee: async (routeId, studentId, routeStopId) => {
    const res = await api.get(`/auth/transport/routes/${routeId}/calculate-student-fee/`, {
      params: { student_id: studentId, route_stop_id: routeStopId }
    });
    return res.data;
  },
  assignStudentToRoute: async (routeId, studentId, routeStopId) => {
    const res = await api.post(`/auth/transport/routes/${routeId}/assign-student/`, {
      student_id: studentId,
      route_stop_id: routeStopId
    });
    return res.data;
  },
  getRouteAssignedStudents: async (routeId) => {
    const res = await api.get(`/auth/transport/routes/${routeId}/assigned-students/`);
    return res.data;
  },
  unassignStudentFromRoute: async (assignmentId) => {
    const res = await api.post(`/auth/transport/routes/unassign-student/${assignmentId}/`);
    return res.data;
  },
  getMyTransport: async () => {
    const res = await api.get('/auth/student/transport/');
    return res.data;
  },
  getParentStudentTransport: async (studentId) => {
    const res = await api.get(`/auth/parent/students/${studentId}/transport/`);
    return res.data;
  },
};

/**
 * Driver Portal & Driver Management API service
 */
export const driverAPI = {
  getProfile: async (userId = null) => {
    const params = userId ? { user_id: userId } : {};
    const res = await api.get('/auth/driver/profile/', { params });
    return res.data;
  },
  updateProfile: async (data) => {
    const res = await api.patch('/auth/driver/profile/', data);
    return res.data;
  },
  getDrivers: async (params = {}) => {
    const res = await api.get('/auth/transport/drivers/', { params });
    return res.data;
  },
  verifyDocument: async (driverUserId, payload) => {
    const res = await api.post(`/auth/transport/drivers/${driverUserId}/verify-doc/`, payload);
    return res.data;
  },
  getEligibleDriversForVehicle: async (vehicleId) => {
    const res = await api.get(`/auth/transport/vehicles/${vehicleId}/eligible-drivers/`);
    return res.data;
  },
  assignDriver: async (vehicleId, driverId) => {
    const res = await api.post(`/auth/transport/vehicles/${vehicleId}/assign-driver/`, { driver_id: driverId });
    return res.data;
  },
  unassignDriver: async (vehicleId) => {
    const res = await api.post(`/auth/transport/vehicles/${vehicleId}/unassign-driver/`);
    return res.data;
  },
};

/**
 * Vehicle Expense & Maintenance API Service
 */
export const vehicleExpenseAPI = {
  getExpenses: async (params = {}) => {
    const res = await api.get('/auth/transport/vehicle-expenses/', { params });
    return res.data;
  },
  getStats: async () => {
    const res = await api.get('/auth/transport/vehicle-expenses/stats/');
    return res.data;
  },
  getExpenseDetail: async (id) => {
    const res = await api.get(`/auth/transport/vehicle-expenses/${id}/`);
    return res.data;
  },
  createExpense: async (data) => {
    const isFormData = data instanceof FormData;
    const config = isFormData ? { headers: { 'Content-Type': 'multipart/form-data' } } : {};
    const res = await api.post('/auth/transport/vehicle-expenses/', data, config);
    return res.data;
  },
  updateExpense: async (id, data) => {
    const isFormData = data instanceof FormData;
    const config = isFormData ? { headers: { 'Content-Type': 'multipart/form-data' } } : {};
    const res = await api.patch(`/auth/transport/vehicle-expenses/${id}/`, data, config);
    return res.data;
  },
  deleteExpense: async (id) => {
    const res = await api.delete(`/auth/transport/vehicle-expenses/${id}/`);
    return res.data;
  },
  submitExpense: async (id) => {
    const res = await api.post(`/auth/transport/vehicle-expenses/${id}/submit/`);
    return res.data;
  },
  financeAction: async (id, payload) => {
    const res = await api.post(`/auth/finance/vehicle-expenses/${id}/action/`, payload);
    return res.data;
  },
  completeExpense: async (id, data) => {
    const isFormData = data instanceof FormData;
    const config = isFormData ? { headers: { 'Content-Type': 'multipart/form-data' } } : {};
    const res = await api.post(`/auth/transport/vehicle-expenses/${id}/complete/`, data, config);
    return res.data;
  },
};

/**
 * Vehicle ODO Meter Tracking & Log API Service
 */
export const transportODOAPI = {
  initializeBaseline: async (data) => {
    const res = await api.post('/auth/transport/odo/initialize-baseline/', data);
    return res.data;
  },
  recordDeparture: async (data) => {
    const res = await api.post('/auth/transport/odo/record-departure/', data);
    return res.data;
  },
  recordArrival: async (data) => {
    const res = await api.post('/auth/transport/odo/record-arrival/', data);
    return res.data;
  },
  recordInterim: async (data) => {
    const isFormData = data instanceof FormData;
    const config = isFormData ? { headers: { 'Content-Type': 'multipart/form-data' } } : {};
    const res = await api.post('/auth/transport/odo/record-interim/', data, config);
    return res.data;
  },
  getDailyStatus: async () => {
    const res = await api.get('/auth/transport/odo/daily-status/');
    return res.data;
  },
  getHistory: async (vehicleId, params = {}) => {
    const res = await api.get(`/auth/transport/odo/vehicle/${vehicleId}/history/`, { params });
    return res.data;
  },
  getCorrectionRequests: async (params = {}) => {
    const res = await api.get('/auth/transport/odo/correction-requests/', { params });
    return res.data;
  },
  submitCorrectionRequest: async (data) => {
    const isFormData = data instanceof FormData;
    const config = isFormData ? { headers: { 'Content-Type': 'multipart/form-data' } } : {};
    const res = await api.post('/auth/transport/odo/correction-request/', data, config);
    return res.data;
  },
  reviewCorrectionRequest: async (id, payload) => {
    const res = await api.post(`/auth/transport/odo/correction-requests/${id}/review/`, payload);
    return res.data;
  },
  getReports: async () => {
    const res = await api.get('/auth/transport/odo/reports/');
    return res.data;
  },
};

export default api;


